import React, { useState, useEffect, useRef } from 'react'
import RuleChatbotModal from './RuleChatbotModal'
import { createPortal } from 'react-dom'
import { FileAnalysis } from '../../types/index'
import type { FileColumn } from '../../types/index'
// Updated column widths: 30%, 50%, 20% - Jan 12, 2026
import { DataType } from '../../constants'
import DataTypeSelector from '../common/DataTypeSelector'
import AutosizeDescription from '../common/AutosizeDescription'
import { RiDeleteBin7Fill, RiSaveFill, RiInformationFill } from "react-icons/ri"
import { IoMdSettings } from "react-icons/io"
import ContextualHelpLink from '../training/ContextualHelpLink'
import { BiSolidInfoCircle } from "react-icons/bi"
import { dqRuleService } from '../../services/dqRuleService'
import { authManager } from '../../services/authManager'
import { useToast } from '../../hooks/useToast'
import { useTranslate } from '../../hooks/useTranslate'
import ErrorBoundary from '../ErrorBoundary'
import {
    FRONTEND_TO_BACKEND_RULE_TYPES,
    BACKEND_TO_FRONTEND_RULE_TYPES,
    sanitizeDescription,
    type BatchCreateRulesRequest,
    type BatchCreateDatabricksRulesRequest,
    type ModernBatchCreateRulesRequest,
    type ReuseRuleRequest,
    type CreateRuleRequest,
    type CreateDatabricksRuleRequest,
    type FrontendRuleType,
    type BackendDQRule
} from '../../types/dqRules'

// Local interface for frontend rules in the modal
interface DQRule {
    id?: string;
    column_name: string;
    rule_type: string;
    rule_logic: string;
    business_meaning: string;
    active_flag: boolean;
    visibility?: 'public' | 'private';
    created_by?: string;
    created_at?: string;
    isFromExisting?: boolean; // For UI logic to distinguish between new and existing rules
    rule_id?: string; // For reusing existing rules
}

// Switch component styles
const switchStyles = `
.switch {
  --_switch-bg-clr: linear-gradient(135deg, #fef2f2, #fee2e2);
  --_switch-padding: 2px;
  --_slider-bg-clr: linear-gradient(135deg, #dc2626, #b91c1c);
  --_slider-bg-clr-on: linear-gradient(135deg, #ef4444, #dc2626);
  --_slider-txt-clr: #ffffff;
  --_label-padding: 0.4rem 0.8rem;
  --_switch-easing: cubic-bezier(0.47, 1.64, 0.41, 0.8);
  color: #7f1d1d;
  width: fit-content;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  position: relative;
  isolation: isolate;
  border-radius: 9999px;
  cursor: pointer;
  min-width: 140px;
  height: 36px;
  box-shadow: 0 2px 4px rgba(220, 38, 38, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.switch input[type="checkbox"] {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.switch > span {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 300ms ease-in-out;
  padding: var(--_label-padding);
  font-size: 0.75rem;
  font-weight: 600;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  z-index: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  line-height: 1;
}

.switch::before,
.switch::after {
  content: "";
  position: absolute;
  border-radius: inherit;
  transition: inset 150ms ease-in-out;
}

.switch::before {
  background: var(--_slider-bg-clr);
  inset: var(--_switch-padding) calc(50% - var(--_switch-padding)) var(--_switch-padding) var(--_switch-padding);
  transition: inset 500ms var(--_switch-easing), background 500ms ease-in-out;
  z-index: 0;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(185, 28, 28, 0.3);
}

.switch::after {
  background: var(--_switch-bg-clr);
  inset: 0;
  z-index: -1;
  border: 2px solid #dc2626;
  box-shadow: inset 0 1px 3px rgba(220, 38, 38, 0.1);
}

.switch:focus-within::after {
  inset: -2px;
  border-color: #b91c1c;
}

.switch:has(input:checked):hover > span:first-of-type,
.switch:has(input:not(:checked)):hover > span:last-of-type {
  opacity: 1;
  transition-delay: 0ms;
  transition-duration: 100ms;
}

.switch:has(input:checked):hover::before {
  inset: var(--_switch-padding) var(--_switch-padding) var(--_switch-padding) calc(45% - var(--_switch-padding));
}

.switch:has(input:not(:checked)):hover::before {
  inset: var(--_switch-padding) calc(45% - var(--_switch-padding)) var(--_switch-padding) var(--_switch-padding);
}

.switch:has(input:checked)::before {
  background: var(--_slider-bg-clr-on);
  inset: var(--_switch-padding) var(--_switch-padding) var(--_switch-padding) calc(50% - var(--_switch-padding));
}

.switch > span:last-of-type,
.switch > input:checked + span:first-of-type {
  opacity: 0.5;
  color: #991b1b;
  font-weight: 500;
}

.switch > input:checked ~ span:last-of-type {
  opacity: 1;
  color: #ffffff;
  font-weight: 700;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.switch > input:not(:checked) + span:first-of-type {
  opacity: 1;
  color: #ffffff;
  font-weight: 700;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.switch > input:checked + span:first-of-type {
  opacity: 0.5;
  color: #991b1b;
  font-weight: 500;
}

.switch > input:not(:checked) ~ span:last-of-type {
  opacity: 0.5;
  color: #991b1b;
  font-weight: 500;
}
`;

interface ColumnConfigurationTableProps {
    analysis: FileAnalysis | null
    onColumnUpdate: (columnName: string, field: string, value: unknown) => void
    dataObjectId?: string // For API calls
    // Note: Removed columnRulesCount - only show user-applied and saved rules
    tableName?: string // For Databricks table rules (alternative to dataObjectId)
    onRulesUpdated?: (columnName: string, newRules: BackendDQRule[]) => void // Callback for updating parent with saved rules
    onSelectionChange?: (selectedColumns: string[]) => void // Notify parent of selected columns for reporting
    initialSelectedColumns?: string[] // Optional initial selection
    existingRules?: BackendDQRule[] // Rules loaded from the backend for this data object
    rulesLoading?: boolean // Loading state for existing rules
    showVisibility?: boolean // Whether to show visibility column (true for databricks, false for upload)
    newColumnNames?: string[] // Column names to highlight as NEW (green border + badge)
}

interface RuleModalProps {
    isOpen: boolean
    onClose: () => void
    columnName: string
    columnDataType: string
    currentRule: string
    onSave: (rule: string) => void
    dataObjectId?: string // For API calls
    tableName?: string // For Databricks table rules
    existingRules?: BackendDQRule[] // Pre-loaded rules from column metadata
    allDqRules?: BackendDQRule[] // All available rules for selection
    onRulesUpdated?: (columnName: string, newRules: BackendDQRule[]) => void // Callback for updating parent with saved rules
    onRuleDeleted?: (columnName: string, ruleId: string) => void // Callback for deleting rule from memory
    columnSample?: unknown[]
    analysis?: FileAnalysis | null // Add analysis prop to access file metadata
    showVisibility?: boolean // Whether to show visibility controls
}

interface RuleConfigSchema {
    type: 'range' | 'list' | 'dropdown' | 'text' | 'boolean' | 'regex' | 'none'
    properties: Record<string, unknown>
    validation: Record<string, unknown>
    defaults: Record<string, unknown>
}

interface RuleCatalogEntry {
    rule_type: string
    label: string
    scope: 'COLUMN' | 'TABLE' | 'FILE'
    allowed_data_types: string[]
    requires_config: boolean
    config_schema: RuleConfigSchema
    business_meaning: string
    impact_description: string
    execution_cost: 'LOW' | 'MEDIUM' | 'HIGH'
    tags: string[]
}

const PRODUCTION_RULE_CATALOG: RuleCatalogEntry[] = [
    {
        rule_type: 'AI_RULE_CREATOR',
        label: 'Create Rules with AI',
        scope: 'COLUMN',
        allowed_data_types: ['STRING', 'INTEGER', 'DECIMAL', 'DATE', 'BOOLEAN', 'OTHER'],
        requires_config: false,
        config_schema: {
            type: 'none',
            properties: {},
            validation: {},
            defaults: {}
        },
        business_meaning: 'Describe your validation requirements in plain language and let AI generate the appropriate rule',
        impact_description: 'Use AI to describe and generate a rule',
        execution_cost: 'LOW' as const,
        tags: ['ai', 'generator']
    },
    {
        rule_type: 'NULL_CHECK',
        label: 'Null Value Check',
        scope: 'COLUMN',
        allowed_data_types: ['STRING', 'INTEGER', 'DECIMAL', 'DATE', 'BOOLEAN', 'OTHER'],
        requires_config: false,
        config_schema: {
            type: 'none',
            properties: {},
            validation: {},
            defaults: {}
        },
        business_meaning: 'Validates that the column contains no null or empty values',
        impact_description: 'Ensures data completeness and prevents missing values',
        execution_cost: 'LOW' as const,
        tags: ['null-validation', 'completeness']
    },
    {
        rule_type: 'RANGE_CHECK',
        label: 'Range Validation',
        scope: 'COLUMN',
        allowed_data_types: ['INTEGER', 'DECIMAL', 'DATE'],
        requires_config: true,
        config_schema: {
            type: 'range',
            properties: {
                min_value: { type: 'number' },
                max_value: { type: 'number' }
            },
            validation: { required: ['min_value', 'max_value'] },
            defaults: {}
        },
        business_meaning: 'Validates that numeric values fall within specified minimum and maximum bounds',
        impact_description: 'Prevents out-of-range values that could cause business errors',
        execution_cost: 'LOW' as const,
        tags: ['range-validation', 'bounds']
    },
    {
        rule_type: 'LOV_CHECK',
        label: 'List of Values Check',
        scope: 'COLUMN',
        allowed_data_types: ['STRING', 'INTEGER', 'DECIMAL', 'DATE', 'BOOLEAN'],
        requires_config: true,
        config_schema: {
            type: 'list',
            properties: {
                values: { type: 'array', items: { type: 'string' } }
            },
            validation: { required: ['values'] },
            defaults: {}
        },
        business_meaning: 'Ensures only values from a predefined whitelist are accepted',
        impact_description: 'Ensures only valid categorical values are accepted',
        execution_cost: 'LOW' as const,
        tags: ['enumeration', 'categorical']
    },
    {
        rule_type: 'ERR_LOV_CHECK',
        label: 'Error List of Values Check',
        scope: 'COLUMN',
        allowed_data_types: ['STRING', 'INTEGER', 'DECIMAL', 'DATE', 'BOOLEAN'],
        requires_config: true,
        config_schema: {
            type: 'list',
            properties: {
                values: { type: 'array', items: { type: 'string' } }
            },
            validation: { required: ['values'] },
            defaults: {}
        },
        business_meaning: 'Rejects values that appear in a predefined blacklist of problematic values',
        impact_description: 'Prevents known problematic values from entering the system',
        execution_cost: 'LOW' as const,
        tags: ['blacklist', 'exclusion']
    },
    {
        rule_type: 'LENGTH_CHECK',
        label: 'Length Validation',
        scope: 'COLUMN',
        allowed_data_types: ['STRING'],
        requires_config: true,
        config_schema: {
            type: 'range',
            properties: {
                min_length: { type: 'integer', minimum: 0 },
                max_length: { type: 'integer', minimum: 1 }
            },
            validation: { required: ['min_length', 'max_length'] },
            defaults: {}
        },
        business_meaning: 'Validates that text fields have appropriate length between minimum and maximum characters',
        impact_description: 'Prevents oversized or undersized text data',
        execution_cost: 'LOW' as const,
        tags: ['length-validation', 'string']
    },
    {
        rule_type: 'INVALID_CHAR_CHECK',
        label: 'Invalid Character Check',
        scope: 'COLUMN',
        allowed_data_types: ['STRING'],
        requires_config: true,
        config_schema: {
            type: 'text',
            properties: {
                invalid_patterns: { type: 'array', items: { type: 'string' } }
            },
            validation: { required: ['invalid_patterns'] },
            defaults: {}
        },
        business_meaning: 'Detects and flags text containing forbidden characters or patterns',
        impact_description: 'Ensures data contains only acceptable characters',
        execution_cost: 'MEDIUM' as const,
        tags: ['character-validation', 'pattern']
    },
    {
        rule_type: 'PK_CHECK',
        label: 'Primary Key Check',
        scope: 'COLUMN',
        allowed_data_types: ['STRING', 'INTEGER', 'DECIMAL', 'DATE', 'BOOLEAN', 'OTHER'],
        requires_config: false,
        config_schema: {
            type: 'none',
            properties: {},
            validation: {},
            defaults: {}
        },
        business_meaning: 'Validates that each value in the column is unique across all rows',
        impact_description: 'Ensures entity uniqueness and referential integrity',
        execution_cost: 'MEDIUM' as const,
        tags: ['primary-key', 'uniqueness']
    },
    {
        rule_type: 'DATA_TYPE_CHECK',
        label: 'Data Type Validation',
        scope: 'COLUMN',
        allowed_data_types: ['STRING', 'INTEGER', 'DECIMAL', 'DATE', 'BOOLEAN', 'OTHER'],
        requires_config: true,
        config_schema: {
            type: 'dropdown',
            properties: {
                expected_type: { type: 'string', enum: ['string', 'integer', 'decimal', 'date'] }
            },
            validation: { required: ['expected_type'] },
            defaults: {}
        },
        business_meaning: 'Verifies that all values match the expected data type format',
        impact_description: 'Prevents type mismatches and parsing errors',
        execution_cost: 'LOW' as const,
        tags: ['type-validation', 'format']
    }
];

// Function to get appropriate icon for each rule type
const getRuleIcon = (ruleType: string) => {
    switch (ruleType) {
        case 'AI_RULE_CREATOR':
            return '🤖';
        case 'NULL_CHECK':
            return '🚫';
        case 'RANGE_CHECK':
            return '📊';
        case 'LOV_CHECK':
            return '✅';
        case 'ERR_LOV_CHECK':
            return '❌';
        case 'LENGTH_CHECK':
            return '📏';
        case 'INVALID_CHAR_CHECK':
            return '🔍';
        case 'PK_CHECK':
            return '🔑';
        case 'DATA_TYPE_CHECK':
            return '🏷️';
        default:
            return '⚙️';
    }
};

const RuleLogicInput: React.FC<{
    ruleType: string,
    value: string,
    onChange: (value: string) => void
}> = ({ ruleType, value, onChange }) => {
    // Lightweight multi-value chips input for list-based rules
    const MultiValueInput: React.FC<{
        values: string[];
        onValuesChange: (vals: string[]) => void;
        placeholder?: string;
        ariaLabel?: string;
    }> = ({ values, onValuesChange, placeholder = 'Add value and press Enter', ariaLabel = 'Multi value input' }) => {
        const [text, setText] = useState('');

        const commit = (raw: string) => {
            const parts = raw
                .split(',') // allow comma as separator too
                .map(v => v.trim())
                .filter(v => v.length > 0);
            if (parts.length === 0) return;
            const next = [...values];
            for (const p of parts) {
                if (!next.includes(p)) next.push(p);
            }
            onValuesChange(next);
            setText('');
        };

        const removeAt = (idx: number) => {
            const next = values.filter((_, i) => i !== idx);
            onValuesChange(next);
        };

        return (
            <div className="w-full p-2 border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 text-sm">
                <div className="flex flex-wrap gap-2">
                    {values.map((v, i) => (
                        <span key={`${v}-${i}`} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800 border">
                            {v}
                            <button type="button" aria-label={`Remove ${v}`} className="ml-2 text-gray-500 hover:text-gray-700" onClick={() => removeAt(i)}>×</button>
                        </span>
                    ))}
                    <input
                        aria-label={ariaLabel}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ',') {
                                e.preventDefault();
                                commit(text);
                            }
                        }}
                        onBlur={() => commit(text)}
                        placeholder={placeholder}
                        className="flex-1 min-w-[160px] outline-none"
                    />
                </div>
            </div>
        );
    };
    const parseValue = (val: string | object) => {
        try {
            if (typeof val === 'object') return val;
            if (!val || val === '') return {};
            return JSON.parse(val);
        } catch {
            return { raw_value: val };
        }
    };

    const config = parseValue(value);

    // Helper to find LLM understanding text (prioritize over SQL for AI-generated rules)
    const findLlmUnderstanding = (cfg: any): string | null => {
        if (!cfg || typeof cfg !== 'object') return null;
        const keys = ['llm_understanding', 'validation_description', 'understanding', 'explanation'];
        for (const k of keys) {
            const v = cfg[k];
            if (typeof v === 'string' && v.trim().length > 0) return v as string;
        }
        return null;
    };

    // Helper to find SQL text under common keys
    const findSqlText = (cfg: any): string | null => {
        if (!cfg || typeof cfg !== 'object') return null;
        const keys = ['custom_sql', 'sql', 'sql_query', 'sqlQuery', 'sql_content', 'query'];
        for (const k of keys) {
            const v = cfg[k];
            if (typeof v === 'string' && v.trim().length > 0) return v as string;
        }
        return null;
    };

    // Global SQL box: if SQL exists in config, always show it regardless of rule type
    // Fallback: detect raw SQL if rule_logic is a plain string containing SQL keywords
    const detectRawSql = (val: unknown): string | null => {
        if (typeof val !== 'string') return null;
        const trimmed = val.trim();
        if (!trimmed) return null;
        const sqlPattern = /(select\s|with\s|insert\s|update\s|delete\s|create\s|alter\s|drop\s)/i;
        return sqlPattern.test(trimmed) ? trimmed : null;
    };

    // Check if this is an AI-generated rule by looking for generated_by_llm flag
    const isAiGenerated = config && typeof config === 'object' && (config as any).generated_by_llm;

    // Priority 1: Show LLM understanding for AI-generated rules
    const llmUnderstanding = findLlmUnderstanding(config as any);
    if (llmUnderstanding && isAiGenerated) {
        return (
            <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-700">AI Rule Understanding</div>
                <div className="w-full max-h-56 overflow-auto p-3 rounded-md bg-red-50 border border-red-200 text-gray-800 text-sm whitespace-pre-wrap">
                    {llmUnderstanding}
                </div>
            </div>
        );
    }

    // Priority 2: Show SQL for non-AI rules or when LLM understanding is not available
    const detectedSqlText = findSqlText(config as any) || detectRawSql(value);
    if (detectedSqlText) {
        const sqlText = detectedSqlText;
        const copyToClipboard = async () => {
            try {
                await navigator.clipboard.writeText(sqlText);
            } catch {
                // clipboard not available; ignore
            }
        };
        return (
            <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-700">SQL Rule Configuration</div>
                <pre className="w-full max-h-56 overflow-auto p-3 rounded-md bg-gray-900 text-gray-100 text-xs whitespace-pre-wrap">
                    {sqlText}
                </pre>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={copyToClipboard}
                        className="px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50"
                        aria-label="Copy SQL to clipboard"
                    >
                        Copy SQL
                    </button>
                </div>
            </div>
        );
    }

    const updateConfig = (newConfig: Record<string, unknown>) => {
        onChange(JSON.stringify(newConfig));
    };

    const ruleDefinition = PRODUCTION_RULE_CATALOG.find(r => r.rule_type === ruleType);

    // Check if this is LLM understanding text (not JSON)
    const isLlmText = value && !value.startsWith('{') && !value.startsWith('[');

    if (!ruleDefinition?.requires_config) {
        if (isLlmText) {
            // Show LLM understanding in a simple text box
            return (
                <div className="text-sm p-2">
                    <textarea
                        className="w-full p-2 border border-gray-300 rounded text-sm resize-none"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        rows={3}
                        placeholder="Rule configuration..."
                    />
                </div>
            );
        }
        return (
            <div className="text-sm text-gray-500 p-2 text-center">
                No configuration required
            </div>
        );
    }

    // Handle the 8 specific rule types
    switch (ruleType) {
        case 'RANGE_CHECK':
            return (
                <div className="space-y-2">
                    <div className="flex space-x-2">
                        <input
                            type="number"
                            step="any"
                            placeholder="Min value"
                            value={config.min_value || ''}
                            onChange={(e) => updateConfig({ ...config, min_value: e.target.value })}
                            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                        <input
                            type="number"
                            step="any"
                            placeholder="Max value"
                            value={config.max_value || ''}
                            onChange={(e) => updateConfig({ ...config, max_value: e.target.value })}
                            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                    </div>
                </div>
            );

        case 'LENGTH_CHECK':
            return (
                <div className="space-y-2">
                    <div className="flex space-x-2">
                        <input
                            type="number"
                            placeholder="Min length"
                            value={config.min_length || ''}
                            onChange={(e) => updateConfig({ ...config, min_length: e.target.value })}
                            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                        <input
                            type="number"
                            placeholder="Max length"
                            value={config.max_length || ''}
                            onChange={(e) => updateConfig({ ...config, max_length: e.target.value })}
                            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                    </div>
                </div>
            );

        case 'LOV_CHECK':
            return (
                <MultiValueInput
                    values={(config.values || []) as string[]}
                    onValuesChange={(vals) => updateConfig({ ...config, values: vals })}
                    placeholder="Add allowed value and press Enter"
                    ariaLabel="Allowed values"
                />
            );

        case 'ERR_LOV_CHECK':
            return (
                <MultiValueInput
                    values={(config.values || []) as string[]}
                    onValuesChange={(vals) => updateConfig({ ...config, values: vals })}
                    placeholder="Add invalid value and press Enter"
                    ariaLabel="Invalid values"
                />
            );

        case 'INVALID_CHAR_CHECK':
            return (
                <MultiValueInput
                    values={(config.invalid_patterns || []) as string[]}
                    onValuesChange={(vals) => updateConfig({ ...config, invalid_patterns: vals })}
                    placeholder="Add invalid character/pattern and press Enter"
                    ariaLabel="Invalid character patterns"
                />
            );

        case 'DATA_TYPE_CHECK':
            return (
                <div>
                    <select
                        value={config.expected_type || ''}
                        onChange={(e) => updateConfig({ expected_type: e.target.value })}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                        <option value="">Select expected type...</option>
                        <option value="string">String</option>
                        <option value="integer">Integer</option>
                        <option value="decimal">Decimal</option>
                        <option value="date">Date</option>
                        <option value="boolean">Boolean</option>
                    </select>
                </div>
            );

        default:
            return (
                <div className="text-sm text-gray-500 p-2 text-center">
                    Configuration for {ruleType} not implemented
                </div>
            );
    }
};

const getAvailableRules = () => {
    // Show all rules regardless of data type (no filtering by allowed_data_types)
    // Order: 1) Create Rules with AI, then others alphabetically
    const priority = (rt: string) => rt === 'AI_RULE_CREATOR' ? 0 : 1;
    return [...PRODUCTION_RULE_CATALOG].sort((a, b) => {
        const pa = priority(a.rule_type);
        const pb = priority(b.rule_type);
        if (pa !== pb) return pa - pb;
        return a.label.localeCompare(b.label);
    });
};
/*
const getAvailableRuleTypes = (dataType: string) => {
    const applicableRules = getAvailableRules(dataType);
    
    return applicableRules.map(rule => ({
        value: rule.rule_type,
        label: rule.label,
        description: rule.business_meaning,
        config_schema: rule.config_schema
    }));
};
*/

const RuleModal: React.FC<RuleModalProps> = ({ isOpen, onClose, columnName, columnDataType, currentRule, onSave, dataObjectId, tableName, existingRules, allDqRules, onRulesUpdated, onRuleDeleted, columnSample, analysis, showVisibility = true }) => {
    const [rules, setRules] = useState<DQRule[]>([]);
    const [originalRules, setOriginalRules] = useState<DQRule[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoadedFromAPI, setHasLoadedFromAPI] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState<{ ruleId: string; ruleIndex: number } | null>(null);
    const [updatingRuleIds, setUpdatingRuleIds] = useState<Set<string>>(new Set());

    const { push: pushToast } = useToast();
    const { t: tRule } = useTranslate();
    const [isSaving, setIsSaving] = useState(false);
    // Removed preSaveConfirm - now directly calling handleSave

    // 🔍 Debug useEffect to track rules state changes
    useEffect(() => {
        console.log('📊 Rules State Changed:');
        console.log('  Total Rules:', rules.length);
        console.log('  Rules with Visibility:', rules.map((rule, index) => ({
            index,
            column: rule.column_name,
            ruleType: rule.rule_type,
            visibility: rule.visibility || 'undefined'
        })));
        console.log('  Full Rules Array:', rules);
    }, [rules]);

    // State for tab navigation and rule management
    const [activeTab, setActiveTab] = useState<'create' | 'existing'>('create');
    // Chatbot modal only
    const [showRuleChatbot, setShowRuleChatbot] = useState<boolean>(false);

    // Reset to create tab when tableName is not available (non-Databricks)
    useEffect(() => {
        if (!tableName && activeTab === 'existing') {
            setActiveTab('create');
        }
    }, [tableName, activeTab]);

    // AI finalize handled inline in onFinalize of RuleChatbotModal
    const [selectedExistingRules, setSelectedExistingRules] = useState<Set<string>>(new Set());
    const [searchFilter, setSearchFilter] = useState('');

    const availableRules = getAvailableRules();

    // Suggestions computation removed per request (Rules Marketplace/Smart Suggestions disabled)

    // Helper functions for existing rules management
    const getCompatibleExistingRules = () => {
        if (!allDqRules) return [];

        // Get current user email for access control
        const currentUserEmail = authManager.getUserEmail();
        if (!currentUserEmail) {
            console.warn('No user email available for rule access filtering');
            return [];
        }

        // Filter rules based on visibility and user access (following API documentation)
        const filtered = allDqRules.filter(rule => {
            // More lenient filtering - show rules that are not soft deleted
            // Use is_active !== false instead of is_active === true to handle undefined/null cases
            const isActive = rule.is_active !== false;
            if (!isActive) return false;

            // Apply visibility-based access control for EXISTING rules:
            // - User can see their own rules (both public and private) for reuse
            // - User can see other people's public rules for discovery/reuse  
            // - User CANNOT see other people's private rules
            const isOwnRule = rule.created_by?.toLowerCase() === currentUserEmail.toLowerCase();
            const isPublicRule = rule.visibility === 'public';

            // EXISTING RULES: Own rules OR other people's public rules
            const hasAccess = isOwnRule || (!isOwnRule && isPublicRule);

            return hasAccess;
        }).filter(rule => {
            // Filter by current column - only show rules for the column we're editing
            if (rule.column_name !== columnName) {
                return false;
            }

            // Apply search filter
            if (!searchFilter.trim()) return true;
            const searchTerm = searchFilter.toLowerCase();
            return (
                (rule.rule_description || '').toLowerCase().includes(searchTerm) ||
                rule.rule_type.toLowerCase().includes(searchTerm) ||
                rule.column_name.toLowerCase().includes(searchTerm)
            );
        });

        return filtered;
    };

    const handleExistingRuleSelect = (ruleId: string) => {
        setSelectedExistingRules(prev => {
            const newSet = new Set(prev);
            if (newSet.has(ruleId)) {
                newSet.delete(ruleId);
            } else {
                newSet.add(ruleId);
            }
            return newSet;
        });
    };

    const applyExistingRules = async () => {
        if (selectedExistingRules.size === 0) {
            pushToast({ variant: 'warning', message: 'Please select at least one rule to apply.' });
            return;
        }

        try {
            setIsSaving(true);
            const rulesToApply = allDqRules?.filter(rule => selectedExistingRules.has(rule.id!)) || [];

            // Transform selected existing rules to frontend format for local display
            const appliedRules = rulesToApply.map(backendRule => ({
                ...transformBackendRule(backendRule),
                column_name: columnName, // Override to show on current column
                id: `temp-${backendRule.id}-${Date.now()}`, // Temporary ID for UI tracking
                created_at: new Date().toISOString(), // Current timestamp
                created_by: backendRule.created_by || 'Current User',
                rule_id: backendRule.id, // Keep reference to original rule for batch API
                isFromExisting: true // Flag to identify rules applied from existing
            }));

            // Add applied rules to current rules display (local state only)
            setRules(prev => [...prev, ...appliedRules]);
            setSelectedExistingRules(new Set());
            setActiveTab('create'); // Switch back to create tab

            pushToast({
                variant: 'success',
                message: `Added ${appliedRules.length} existing rule(s) to ${columnName}. Click Generate Report to save changes.`
            });
        } catch (error) {
            console.error('Failed to apply existing rules:', error);
            pushToast({ variant: 'error', message: 'Failed to apply selected rules. Please try again.' });
        } finally {
            setIsSaving(false);
        }
    };

    // Function to transform backend rule to frontend format
    const transformBackendRule = (backendRule: BackendDQRule): DQRule => {
        const frontendRuleType = BACKEND_TO_FRONTEND_RULE_TYPES[backendRule.rule_type] || 'NULL_CHECK';

        // is_active = soft delete flag (true = exists, false = deleted)
        // is_enabled = enable/disable flag (true = enabled, false = disabled)
        // We only process rules that are active (not soft deleted)

        return {
            id: backendRule.id,
            column_name: backendRule.column_name,
            rule_type: frontendRuleType,
            rule_logic: JSON.stringify(backendRule.parameters || {}),
            business_meaning: backendRule.rule_description || '',
            active_flag: backendRule.is_enabled === true, // Strict check: only true when explicitly enabled
            visibility: backendRule.visibility || 'public', // Preserve backend visibility, default to public
            created_by: backendRule.created_by,
            created_at: backendRule.created_at
        };
    };

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setHasLoadedFromAPI(false);
            setRules([]);
            setOriginalRules([]);
            setUpdatingRuleIds(new Set());
            setDeleteConfirmation(null);
            setIsSaving(false);
            // Reset state for all tabs
            setActiveTab('create');
            setSelectedExistingRules(new Set());
            setSearchFilter('');
        }
    }, [isOpen]);

    // Load existing rules from column metadata when modal opens
    useEffect(() => {
        if (isOpen && !hasLoadedFromAPI) {

            if (existingRules) {
                // Use existing rules from column metadata (no API call needed)
                setIsLoading(true);
                try {
                    // Filter active rules for this column
                    const columnRules = existingRules.filter(rule =>
                        rule.column_name === columnName && rule.is_active === true
                    );

                    if (columnRules.length > 0) {
                        // Transform backend rules to frontend format
                        const transformedRules = columnRules.map(transformBackendRule);
                        setRules(transformedRules);
                        setOriginalRules(JSON.parse(JSON.stringify(transformedRules))); // Deep copy for comparison
                    } else {
                        // No existing rules, create default empty rule
                        setRules([{
                            column_name: columnName,
                            rule_type: '',
                            rule_logic: '',
                            business_meaning: '',
                            active_flag: true,
                            visibility: 'public', // Default to public for new rules
                            created_by: 'current_user',
                            created_at: new Date().toISOString()
                        }]);
                        setOriginalRules([]); // No original rules since this is new
                    }
                    setHasLoadedFromAPI(true);
                    setIsLoading(false);
                } catch (error) {
                    console.error('Failed to load existing rules from metadata:', error);
                    // Fallback to empty rule on error
                    setRules([{
                        column_name: columnName,
                        rule_type: '',
                        rule_logic: '',
                        business_meaning: '',
                        active_flag: true,
                        visibility: 'public', // Default to public for new rules
                        created_by: 'current_user',
                        created_at: new Date().toISOString()
                    }]);
                    setOriginalRules([]);
                    setHasLoadedFromAPI(true);
                    setIsLoading(false);
                    pushToast({
                        variant: 'warning',
                        message: 'Could not load existing rules. Starting with empty rule.'
                    });
                }
            } else {
                // No existing rules data provided, create default empty rule
                setRules([{
                    column_name: columnName,
                    rule_type: '',
                    rule_logic: '',
                    business_meaning: '',
                    active_flag: true,
                    visibility: 'public', // Default to public for new rules
                    created_by: 'current_user',
                    created_at: new Date().toISOString()
                }]);
                setOriginalRules([]);
                setHasLoadedFromAPI(true);
                setIsLoading(false);
            }
        }
    }, [isOpen, hasLoadedFromAPI, existingRules, columnName, pushToast]);
    useEffect(() => {
        if (!isOpen) {
            setRules([]);
            setOriginalRules([]);
            setHasLoadedFromAPI(false);
            setIsLoading(false);
            setIsSaving(false);
        }
    }, [isOpen]);

    // Use currentRule if available and no API data loaded yet
    useEffect(() => {
        if (isOpen && !hasLoadedFromAPI && currentRule && !existingRules) {
            try {
                setRules(JSON.parse(currentRule));
                setOriginalRules([]);
            } catch {
                // Fallback for old string format
                setRules([{
                    column_name: columnName,
                    rule_type: '',
                    rule_logic: currentRule,
                    business_meaning: '',
                    active_flag: true,
                    visibility: 'public', // Default to public for new rules
                    created_by: 'current_user',
                    created_at: new Date().toISOString()
                }]);
            }
            setHasLoadedFromAPI(true);
        }
    }, [isOpen, hasLoadedFromAPI, currentRule, existingRules, columnName]);

    // Reset loading state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setHasLoadedFromAPI(false);
            setIsLoading(false);
        }
    }, [isOpen]);

    // 🔧 Disable background scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = '15px'; // Prevent layout shift
        } else {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        }

        // Cleanup on unmount
        return () => {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, [isOpen]);

    const addNewRule = () => {
        setRules([...rules, {
            column_name: columnName,
            rule_type: '',
            rule_logic: '',
            business_meaning: '',
            active_flag: true,
            visibility: 'public', // Default to public for new rules
            created_by: 'current_user',
            created_at: new Date().toISOString()
        }]);
    };

    // Note: individual update API is handled during Save to keep UX simple

    // Delete individual rule (for existing rules with IDs)
    const deleteIndividualRule = async (ruleId: string, ruleIndex: number) => {
        if (!dataObjectId || !ruleId || ruleId.startsWith('temp_')) {
            // For new rules without real IDs or temporary IDs, just remove from local state
            removeRule(ruleIndex);
            setDeleteConfirmation(null); // Close dialog after removing temporary rule
            return;
        }

        setUpdatingRuleIds(prev => new Set([...prev, ruleId]));

        try {
            await dqRuleService.deleteRule(ruleId);

            // Remove from local state
            setRules(prev => prev.filter((_, idx) => idx !== ruleIndex));

            // Also remove from saved rules memory via callback
            if (onRuleDeleted) {
                onRuleDeleted(columnName, ruleId);
            }

            pushToast({
                variant: 'success',
                message: 'Rule deleted successfully!'
            });
        } catch (error) {
            console.error('Failed to delete rule:', error);
            pushToast({
                variant: 'error',
                message: 'Failed to delete rule. Please try again.'
            });
        } finally {
            // Always close the confirmation dialog and clear updating state
            setDeleteConfirmation(null);
            setUpdatingRuleIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(ruleId);
                return newSet;
            });
        }
    };

    // Helper function to transform rule parameters to correct types
    const transformRuleParameters = (ruleType: string, parametersString: string): Record<string, unknown> => {
        let obj: Record<string, unknown> = {};
        try {
            obj = JSON.parse(parametersString || '{}') as Record<string, unknown>;
        } catch {
            obj = {};
        }

        if (ruleType === 'RANGE_CHECK') {
            const minRaw = (obj as Record<string, unknown>).min_value;
            const maxRaw = (obj as Record<string, unknown>).max_value;
            return {
                ...obj,
                min_value: typeof minRaw === 'string' ? parseFloat(minRaw) : minRaw,
                max_value: typeof maxRaw === 'string' ? parseFloat(maxRaw) : maxRaw
            } as Record<string, unknown>;
        }

        if (ruleType === 'LENGTH_CHECK') {
            const minRaw = (obj as Record<string, unknown>).min_length;
            const maxRaw = (obj as Record<string, unknown>).max_length;
            return {
                ...obj,
                min_length: typeof minRaw === 'string' ? parseInt(minRaw) : minRaw,
                max_length: typeof maxRaw === 'string' ? parseInt(maxRaw) : maxRaw
            } as Record<string, unknown>;
        }

        // Map frontend config keys to backend-expected parameter keys
        if (ruleType === 'LOV_CHECK') {
            const values = (obj as Record<string, unknown>).values;
            return {
                values: Array.isArray(values) ? values : []
            } as Record<string, unknown>;
        }

        if (ruleType === 'ERR_LOV_CHECK') {
            const values = (obj as Record<string, unknown>).values;
            return {
                error_values: Array.isArray(values) ? values : []
            } as Record<string, unknown>;
        }

        if (ruleType === 'INVALID_CHAR_CHECK') {
            const patterns = (obj as Record<string, unknown>).invalid_patterns;
            return {
                invalid_chars: Array.isArray(patterns) ? patterns : []
            } as Record<string, unknown>;
        }

        return obj;
    };

    // Helper function to detect if a rule has changed
    const hasRuleChanged = (currentRule: DQRule, originalRule: DQRule): boolean => {
        if (!originalRule) return true; // New rule, treat as changed

        // Note: rule_type is excluded since we don't allow type changes for existing rules
        return (
            currentRule.rule_logic !== originalRule.rule_logic ||
            currentRule.business_meaning !== originalRule.business_meaning ||
            currentRule.active_flag !== originalRule.active_flag
        );
    };

    const removeRule = (index: number) => {
        // Get the rule being removed to check if it should be removed from memory too
        const ruleToRemove = rules[index];

        const updated = rules.filter((_, i) => i !== index);
        setRules(updated);

        // If the rule has a temp ID (was previously saved to memory), remove it from there too
        if (ruleToRemove?.id && ruleToRemove.id.startsWith('temp_') && onRuleDeleted) {
            onRuleDeleted(columnName, ruleToRemove.id);
        }
    };

    const updateRule = (index: number, field: keyof DQRule, value: unknown) => {
        const newRules = [...rules];
        // Do not overwrite description when rule type changes; preserve user-entered text
        newRules[index] = { ...newRules[index], [field]: value };

        // 🔍 Console logging for switch state tracking
        if (field === 'visibility') {
            console.log('🔄 DQ Rule Visibility Switch Changed:');
            console.log('  Column:', newRules[index].column_name);
            console.log('  Rule Type:', newRules[index].rule_type);
            console.log('  Old Visibility:', rules[index]?.visibility || 'undefined');
            console.log('  New Visibility:', value);
            console.log('  Full Rule Object:', newRules[index]);
            console.log('  All Rules State:', newRules);
        }

        setRules(newRules);
    };

    const handleSave = async () => {
        console.log('🔥 [Rule Modal] handleSave called:', {
            columnName,
            rulesCount: rules.length,
            dataObjectId,
            tableName,
            hasExistingRules: existingRules?.length || 0
        });

        try {
            setIsSaving(true);

            // Filter valid rules - handle rules that don't require configuration
            const validRules = rules.filter(rule => {
                const ruleTypeValid = Boolean(rule.rule_type?.trim());
                const ruleDescriptionValid = Boolean(rule.business_meaning?.trim()); // Rule description is mandatory

                // For rules that don't require config, auto-populate empty rule_logic
                const ruleEntry = PRODUCTION_RULE_CATALOG.find(r => r.rule_type === rule.rule_type);
                const requiresConfig = ruleEntry?.requires_config !== false;

                let ruleLogicValid;
                if (!requiresConfig) {
                    // Auto-populate empty rule_logic for rules that don't need it
                    if (!rule.rule_logic?.trim()) {
                        rule.rule_logic = '{}';
                    }
                    ruleLogicValid = true; // Always valid for config-free rules
                } else {
                    ruleLogicValid = Boolean(rule.rule_logic?.trim());
                }

                // NOTE: active_flag should NOT determine validity - disabled rules should still be saved
                // A rule is valid if it has a type, description, and proper configuration

                return ruleTypeValid && ruleDescriptionValid && ruleLogicValid;
            });

            if (validRules.length === 0) {
                // Check if there are rules with missing descriptions
                const rulesWithMissingDescriptions = rules.filter(rule =>
                    rule.rule_type && !rule.business_meaning?.trim()
                );

                if (rulesWithMissingDescriptions.length > 0) {
                    pushToast({
                        variant: 'error',
                        message: 'Please enter rule descriptions for all rules before saving.'
                    });
                    setIsSaving(false);
                    return;
                }

                // Allow saving with zero rules: clear column rules and update parent immediately
                const serializedRules = '';
                onSave(serializedRules);
                onSave(serializedRules);

                if (onRulesUpdated) {
                    onRulesUpdated(columnName, []);
                }

                pushToast({
                    variant: 'success',
                    message: 'Saved 0 rule(s)'
                });
                setIsSaving(false);
                onClose();
                return;
            }

            // If we have dataObjectId, handle existing and new rules separately
            if (dataObjectId && validRules.length > 0) {
                // Split rules into existing (with real backend IDs) and new (without IDs or temp IDs)
                const existingRules = validRules.filter(rule => rule.id && !rule.id.startsWith('temp_'));
                const newRules = validRules.filter(rule => !rule.id || rule.id.startsWith('temp_'));

                let updateResults = { successful: 0, failed: 0 };
                let createResults = { total_created: 0, total_failed: 0 };

                // Update existing rules using individual update API (only changed ones)
                if (existingRules.length > 0) {
                    for (let i = 0; i < existingRules.length; i++) {
                        const rule = existingRules[i];
                        const originalRule = originalRules.find(orig => orig.id === rule.id);

                        // Only update if the rule has actually changed
                        const hasChanged = hasRuleChanged(rule, originalRule!);

                        if (hasChanged) {
                            try {
                                // Prepare complete update request
                                const transformedParameters = transformRuleParameters(rule.rule_type, rule.rule_logic || '{}');

                                // 🔍 Extract SQL from rule_logic if it contains SQL (same logic as creation path)
                                let sqlQuery: string | undefined;
                                try {
                                    const ruleLogic = JSON.parse(rule.rule_logic || '{}');
                                    if (ruleLogic.custom_sql || ruleLogic.sql || ruleLogic.sql_query || ruleLogic.query) {
                                        sqlQuery = ruleLogic.custom_sql || ruleLogic.sql || ruleLogic.sql_query || ruleLogic.query;
                                    }
                                } catch {
                                    // Check if rule_logic is raw SQL string
                                    const sqlPattern = /(select\s|with\s|insert\s|update\s|delete\s|create\s|alter\s|drop\s)/i;
                                    if (typeof rule.rule_logic === 'string' && sqlPattern.test(rule.rule_logic)) {
                                        sqlQuery = rule.rule_logic;
                                    }
                                }

                                // ✅ Ensure SQL is stored inside parameters per documentation (update path)
                                const finalParameters = { ...transformedParameters } as Record<string, unknown>;
                                if (sqlQuery) {
                                    // Use custom_sql for AI-generated rules per documentation
                                    finalParameters.custom_sql = sqlQuery;
                                    finalParameters.validation_description = rule.business_meaning || 'AI-generated validation rule';
                                    finalParameters.generated_by_llm = true;
                                    if (rule.rule_logic) {
                                        try {
                                            const ruleLogic = JSON.parse(rule.rule_logic);
                                            if (ruleLogic.original_prompt) {
                                                finalParameters.original_prompt = ruleLogic.original_prompt;
                                            }
                                        } catch {
                                            // Skip original_prompt if parsing fails
                                        }
                                    }
                                }

                                const updateRequest: { rule_description?: string; is_enabled?: boolean } = {
                                    rule_description: sanitizeDescription(rule.business_meaning || ''),
                                    is_enabled: rule.active_flag
                                };

                                await dqRuleService.updateRule(rule.id!, updateRequest);
                                updateResults.successful++;
                            } catch (error) {
                                console.error('Update failed:', error);
                                updateResults.failed++;
                            }
                        }
                    }
                }

                // Create new rules using batch create API
                if (newRules.length > 0) {
                    try {

                        // Split new rules into truly new rules vs rules applied from existing
                        const trulyNewRules = newRules.filter(rule => !rule.isFromExisting);
                        const rulesFromExisting = newRules.filter(rule => rule.isFromExisting);

                        let createResults = { total_created: 0, total_failed: 0 };

                        // Create truly new rules
                        if (trulyNewRules.length > 0) {
                            // 🚀 Get user email for DQ Rules API
                            const userEmail = authManager.getUserEmail();
                            console.log('🔍 User Email Check:', userEmail);

                            if (!userEmail) {
                                console.error('❌ User email not available from authManager');
                                pushToast({
                                    variant: 'error',
                                    message: 'User email not available. Please log in again.'
                                });
                                throw new Error('User email not available. Please log in again.');
                            }

                            console.log('🚀 Saving DQ Rules with visibility settings...');
                            console.log('  User Email:', userEmail);
                            console.log('  Data Object ID:', dataObjectId);
                            console.log('  Table Name:', tableName);
                            console.log('  Rules with visibility:', trulyNewRules.map(rule => ({
                                column: rule.column_name,
                                type: rule.rule_type,
                                visibility: rule.visibility || 'private',
                                business_meaning: rule.business_meaning
                            })));

                            if (tableName && dataObjectId) {
                                // 🚀 Create payload with visibility fields - user_email in URL, not in payload
                                const createRulesRequest = {
                                    rules: trulyNewRules.map(rule => {
                                        const transformedParameters = transformRuleParameters(rule.rule_type, rule.rule_logic || '{}');

                                        // 🔍 Extract SQL from rule_logic if it contains SQL
                                        let sqlQuery: string | undefined;
                                        try {
                                            const ruleLogic = JSON.parse(rule.rule_logic || '{}');
                                            if (ruleLogic.custom_sql || ruleLogic.sql || ruleLogic.sql_query || ruleLogic.query) {
                                                sqlQuery = ruleLogic.custom_sql || ruleLogic.sql || ruleLogic.sql_query || ruleLogic.query;
                                            }
                                        } catch {
                                            // Check if rule_logic is raw SQL string
                                            const sqlPattern = /(select\s|with\s|insert\s|update\s|delete\s|create\s|alter\s|drop\s)/i;
                                            if (typeof rule.rule_logic === 'string' && sqlPattern.test(rule.rule_logic)) {
                                                sqlQuery = rule.rule_logic;
                                            }
                                        }

                                        // ✅ Ensure SQL is stored inside parameters per documentation
                                        const finalParameters = { ...transformedParameters } as Record<string, unknown>;
                                        if (sqlQuery) {
                                            // Use custom_sql for AI-generated rules per documentation
                                            finalParameters.custom_sql = sqlQuery;
                                            finalParameters.validation_description = rule.business_meaning || 'AI-generated validation rule';
                                            finalParameters.generated_by_llm = true;
                                            if (rule.rule_logic) {
                                                try {
                                                    const ruleLogic = JSON.parse(rule.rule_logic);
                                                    if (ruleLogic.original_prompt) {
                                                        finalParameters.original_prompt = ruleLogic.original_prompt;
                                                    }
                                                } catch {
                                                    // Ignore parsing errors for original_prompt
                                                }
                                            }
                                        }

                                        return {
                                            rule_type: FRONTEND_TO_BACKEND_RULE_TYPES[rule.rule_type as FrontendRuleType] || 'null_check',
                                            parameters: finalParameters,
                                            rule_description: sanitizeDescription(rule.business_meaning || ''),
                                            data_object_id: dataObjectId,
                                            table_name: tableName,
                                            column_name: columnName,
                                            // user_email: userEmail, // Removed - now in URL query parameter
                                            visibility: rule.visibility || 'public', // 🎯 Default to public for new rules
                                            creation_method: 'ai_generated' as const // 🎯 AI-generated rules
                                        };
                                    })
                                } as any; // Cast to bypass TypeScript type restrictions

                                console.log('🚀 Databricks API Payload (user_email in URL):', createRulesRequest);
                                const response = await dqRuleService.createRulesBatch(createRulesRequest);
                                createResults.total_created += response.total_created;
                                createResults.total_failed += response.total_failed;
                            } else if (dataObjectId) {
                                // 🚀 Create payload with visibility fields - user_email in URL, not in payload
                                const createRulesRequest = {
                                    rules: trulyNewRules.map(rule => {
                                        const transformedParameters = transformRuleParameters(rule.rule_type, rule.rule_logic || '{}');

                                        // 🔍 Extract SQL from rule_logic if it contains SQL
                                        let sqlQuery: string | undefined;
                                        try {
                                            const ruleLogic = JSON.parse(rule.rule_logic || '{}');
                                            if (ruleLogic.custom_sql || ruleLogic.sql || ruleLogic.sql_query || ruleLogic.query) {
                                                sqlQuery = ruleLogic.custom_sql || ruleLogic.sql || ruleLogic.sql_query || ruleLogic.query;
                                            }
                                        } catch {
                                            // Check if rule_logic is raw SQL string
                                            const sqlPattern = /(select\s|with\s|insert\s|update\s|delete\s|create\s|alter\s|drop\s)/i;
                                            if (typeof rule.rule_logic === 'string' && sqlPattern.test(rule.rule_logic)) {
                                                sqlQuery = rule.rule_logic;
                                            }
                                        }

                                        // ✅ Ensure SQL is stored inside parameters per documentation
                                        const finalParameters = { ...transformedParameters } as Record<string, unknown>;
                                        if (sqlQuery) {
                                            // Use custom_sql for AI-generated rules per documentation
                                            finalParameters.custom_sql = sqlQuery;
                                            finalParameters.validation_description = rule.business_meaning || 'AI-generated validation rule';
                                            finalParameters.generated_by_llm = true;
                                            if (rule.rule_logic) {
                                                try {
                                                    const ruleLogic = JSON.parse(rule.rule_logic);
                                                    if (ruleLogic.original_prompt) {
                                                        finalParameters.original_prompt = ruleLogic.original_prompt;
                                                    }
                                                } catch {
                                                    // Ignore parsing errors for original_prompt
                                                }
                                            }
                                        }

                                        return {
                                            rule_type: FRONTEND_TO_BACKEND_RULE_TYPES[rule.rule_type as FrontendRuleType] || 'null_check',
                                            parameters: finalParameters,
                                            rule_description: sanitizeDescription(rule.business_meaning || ''),
                                            data_object_id: dataObjectId,
                                            column_name: columnName,
                                            // user_email: userEmail, // Removed - now in URL query parameter
                                            visibility: rule.visibility || 'public', // 🎯 Default to public for new rules
                                            creation_method: 'ai_generated' as const // 🎯 AI-generated rules
                                        };
                                    })
                                } as any; // Cast to bypass TypeScript type restrictions

                                console.log('🚀 Standard API Payload (user_email in URL):', createRulesRequest);

                                // 🔍 DETAILED PAYLOAD DEBUG
                                console.log('🔍 COMPONENT PAYLOAD DEBUG:');
                                console.log('  Rules count:', createRulesRequest.rules.length);
                                if (createRulesRequest.rules.length > 0) {
                                    const firstRule = createRulesRequest.rules[0];
                                    console.log('  First rule details:');
                                    console.log('    rule_type:', firstRule.rule_type);
                                    console.log('    parameters:', firstRule.parameters);
                                    console.log('    rule_description:', firstRule.rule_description);
                                    console.log('    data_object_id:', firstRule.data_object_id);
                                    console.log('    column_name:', firstRule.column_name);
                                    console.log('    visibility:', firstRule.visibility);
                                    console.log('    creation_method:', firstRule.creation_method);
                                    console.log('  Expected API format check:');
                                    console.log('    Has required fields:', {
                                        rule_type: !!firstRule.rule_type,
                                        parameters: !!firstRule.parameters,
                                        rule_description: !!firstRule.rule_description,
                                        data_object_id: !!firstRule.data_object_id,
                                        column_name: !!firstRule.column_name,
                                        visibility: !!firstRule.visibility,
                                        creation_method: !!firstRule.creation_method
                                    });
                                }

                                console.log('🚀 [API CALL] About to call dqRuleService.createRulesBatch...');
                                const response = await dqRuleService.createRulesBatch(createRulesRequest);
                                console.log('🚀 [API CALL] createRulesBatch response received:', response);
                                createResults.total_created += response.total_created;
                                createResults.total_failed += response.total_failed;
                            }
                        }

                        // Apply rules from existing ones using modern create-or-reuse API
                        if (rulesFromExisting.length > 0) {
                            if (tableName && dataObjectId) {
                                const existingRulesRequest: ModernBatchCreateRulesRequest = {
                                    rules: rulesFromExisting.map(rule => ({
                                        rule_id: rule.rule_id!,
                                        data_object_id: dataObjectId,
                                        table_name: tableName,
                                        column_name: columnName
                                    } as ReuseRuleRequest))
                                };
                                const response = await dqRuleService.createRulesBatchModern(existingRulesRequest);
                                createResults.total_created += response.created_count + response.reused_count;
                                createResults.total_failed += response.failed_count;
                            } else if (dataObjectId) {
                                const existingRulesRequest: ModernBatchCreateRulesRequest = {
                                    rules: rulesFromExisting.map(rule => ({
                                        rule_id: rule.rule_id!,
                                        data_object_id: dataObjectId,
                                        column_name: columnName
                                    } as ReuseRuleRequest))
                                };
                                const response = await dqRuleService.createRulesBatchModern(existingRulesRequest);
                                createResults.total_created += response.created_count + response.reused_count;
                                createResults.total_failed += response.failed_count;
                            }
                        }

                        if (trulyNewRules.length === 0 && rulesFromExisting.length === 0) {
                            throw new Error('dataObjectId is required for DQ rule creation');
                        }

                    } catch (error) {
                        console.error('Batch create failed:', error);
                        // Show specific error message to user
                        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                        pushToast({
                            variant: 'error',
                            message: `Failed to create new rules: ${errorMessage}`
                        });
                        createResults.total_failed = newRules.length;
                        setIsSaving(false); // Reset saving state
                        return; // Don't continue if create failed
                    }
                }

                // Show combined results toast
                const totalSuccessful = updateResults.successful + createResults.total_created;
                const totalFailed = updateResults.failed + createResults.total_failed;

                if (totalFailed > 0) {
                    pushToast({
                        variant: 'warning',
                        message: `${totalSuccessful} rule(s) saved successfully. ${totalFailed} failed.`
                    });
                } else {
                    pushToast({
                        variant: 'success',
                        message: `Successfully saved ${totalSuccessful} rule(s)`
                    });
                }

                // Update parent with all rules (keep existing format for now)
                const serializedRules = JSON.stringify(validRules);
                onSave(serializedRules);

                // Always notify parent component with updated rules for in-memory storage
                // This ensures newly saved rules are immediately available in UI
                if (onRulesUpdated) {
                    // Transform newly created/updated rules to backend format for storage
                    const updatedBackendRules = validRules.map(rule => {
                        const ruleEntry = PRODUCTION_RULE_CATALOG.find(r => r.rule_type === rule.rule_type);
                        return {
                            id: rule.id || `temp_${Date.now()}_${Math.random()}`, // Generate temp ID for new rules
                            column_name: columnName,
                            rule_type: FRONTEND_TO_BACKEND_RULE_TYPES[rule.rule_type as FrontendRuleType] || 'null_check',
                            parameters: (() => {
                                try {
                                    return rule.rule_logic && rule.rule_logic.trim() ? JSON.parse(rule.rule_logic) : {};
                                } catch (error) {
                                    console.warn('⚠️ Invalid rule_logic JSON for rule:', rule.rule_type, 'Content:', rule.rule_logic);
                                    return {}; // Fallback to empty parameters for AI-generated rules
                                }
                            })(),
                            rule_description: rule.business_meaning || '',
                            is_active: rule.active_flag,
                            is_enabled: rule.active_flag,
                            created_by: rule.created_by || 'current_user',
                            created_at: rule.created_at || new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                            business_meaning: rule.business_meaning || '',
                            impact_description: ruleEntry?.impact_description || '',
                            execution_cost: ruleEntry?.execution_cost || 'LOW',
                            tags: ruleEntry?.tags || [],
                            data_object_id: dataObjectId || '',
                            visibility: rule.visibility || 'public' // 🔧 Preserve visibility setting
                        } as BackendDQRule;
                    });
                    onRulesUpdated(columnName, updatedBackendRules);
                }
            } else {
                // Fallback to local storage if no dataObjectId (pre-Databricks or file upload flow)
                const serializedRules = validRules.length > 0 ? JSON.stringify(validRules) : '';
                onSave(serializedRules);

                // Also notify parent component with updated rules for in-memory storage
                // This ensures newly configured rules appear immediately in the UI
                if (onRulesUpdated && validRules.length > 0) {
                    const updatedBackendRules = validRules.map(rule => {
                        const ruleEntry = PRODUCTION_RULE_CATALOG.find(r => r.rule_type === rule.rule_type);
                        return {
                            id: rule.id || `temp_${Date.now()}_${Math.random()}`,
                            column_name: columnName,
                            rule_type: FRONTEND_TO_BACKEND_RULE_TYPES[rule.rule_type as FrontendRuleType] || 'null_check',
                            parameters: (() => {
                                try {
                                    return rule.rule_logic && rule.rule_logic.trim() ? JSON.parse(rule.rule_logic) : {};
                                } catch (error) {
                                    console.warn('⚠️ Invalid rule_logic JSON for rule:', rule.rule_type, 'Content:', rule.rule_logic);
                                    return {}; // Fallback to empty parameters for AI-generated rules
                                }
                            })(),
                            rule_description: rule.business_meaning || '',
                            is_active: rule.active_flag,
                            is_enabled: rule.active_flag,
                            created_by: rule.created_by || 'current_user',
                            created_at: rule.created_at || new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                            business_meaning: rule.business_meaning || '',
                            impact_description: ruleEntry?.impact_description || '',
                            execution_cost: ruleEntry?.execution_cost || 'LOW',
                            tags: ruleEntry?.tags || [],
                            data_object_id: '', // Not yet available in this pre-generation flow
                            visibility: rule.visibility || 'public' // 🔧 Preserve visibility setting
                        } as BackendDQRule;
                    });
                    onRulesUpdated(columnName, updatedBackendRules);
                }

                pushToast({
                    variant: 'success',
                    message: `Successfully configured ${validRules.length} rule(s)`
                });
            }

            onClose();

        } catch (error) {
            console.error('Failed to save rules:', error);
            // Show specific error details to user
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            pushToast({
                variant: 'error',
                message: `Failed to save rules: ${errorMessage}. Please try again.`
            });
        } finally {
            setIsSaving(false);
        }
    };

    // 🚀 Only render if open - Portal handles the rest
    if (!isOpen) return null;

    // Ensure Cancel discards changes and restores original rules in parent
    const handleCancel = () => {
        try {
            // Restore local state to original snapshot
            setRules(originalRules);
            // Propagate restore to parent so column indicators revert
            const serialized = originalRules.length > 0 ? JSON.stringify(originalRules) : '';
            onSave(serialized);
        } catch (e) {
            // Ignore serialization issues on cancel
        } finally {
            onClose();
        }
    };

    return createPortal(
        <div
            className={`fixed inset-0 z-[9999] bg-gray-800 bg-opacity-60 backdrop-blur-sm flex items-center justify-center transition-all duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'
                }`}
            style={{ pointerEvents: 'all' }}
            onClick={() => { }} // Disabled backdrop click to prevent accidental closes
        >
            <div
                className={`bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 ease-out w-[90vw] max-w-[1200px] max-h-[90vh] ${isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
                    }`}
                style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)',
                    boxShadow: '0 25px 50px -12px rgba(220, 38, 38, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.8)',
                    pointerEvents: 'all'
                }}
                onClick={(e) => e.stopPropagation()} // Prevent backdrop click when clicking modal content
            >
                {/* Fancy header with red gradient */}
                <div className="bg-gradient-to-r from-red-600 via-rose-600 to-red-800 px-6 py-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 flex items-center justify-center">
                                <IoMdSettings size={24} className="text-white" />
                            </div>
                            <h3 className="text-xl font-semibold text-white">
                                {tRule('Configure Data Quality Rules for')}{' '}
                                <span className="text-red-200 font-mono notranslate">{columnName}</span>
                            </h3>
                        </div>
                        <div className="flex items-center space-x-3">
                            {/* Data type badge in header (right side) */}
                            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white bg-opacity-10 text-red-100 border border-red-300">
                                <span className="w-2 h-2 bg-red-300 rounded-full mr-2"></span>
                                {tRule('Data Type:')} <span className="font-mono ml-1 notranslate">{columnDataType.toUpperCase()}</span>
                            </div>
                            <ContextualHelpLink videoId="tv-003" variant="icon-light" />
                            <button
                                onClick={handleCancel}
                                className="text-white hover:text-red-200 transition-all duration-200 p-2 hover:bg-white hover:bg-opacity-10 rounded-full"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="p-6 bg-white overflow-y-auto max-h-[calc(90vh-100px)]">

                    {/* Tab Navigation - unified style with AddDataSourceModal */}
                    <div className="mb-6">
                        <div className="modal-tabs">
                            <button
                                type="button"
                                onClick={() => setActiveTab('create')}
                                className={`tab-button transition-all duration-200 ${activeTab === 'create' ? 'tab-button--active' : ''}`}
                            >
                                {tRule('Create Rules')}
                            </button>
                            {/* Only show existing rules tab for Databricks tables */}
                            {tableName && (
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('existing')}
                                    className={`tab-button transition-all duration-200 ${activeTab === 'existing' ? 'tab-button--active' : ''}`}
                                >
                                    <span className="inline-flex items-center">
                                        {tRule('Use Existing Rules')}
                                        {allDqRules && allDqRules.length > 0 && (
                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {getCompatibleExistingRules().length}
                                            </span>
                                        )}
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Loading state */}
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mb-4"></div>
                            <p className="text-gray-600 text-sm">{tRule('Loading existing rules...')}</p>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'create' && (
                                <>
                                    {/* Rules section with enhanced styling */}
                                    <div className="mb-6">
                                        <div className="flex justify-between items-center mb-6">
                                            <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                                                {tRule('Active Data Quality Rules')}
                                                {dataObjectId && (
                                                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        API Loaded
                                                    </span>
                                                )}
                                            </h4>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={addNewRule}
                                                    className="flex items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                                                >
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                    </svg>
                                                    {tRule('Add Rule')}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Guard the AI modal with ErrorBoundary to prevent app-wide crash */}
                                        <ErrorBoundary fallback={<div className="p-4 text-sm text-red-700">AI rule helper encountered an error. Please close and reopen.</div>}>
                                            <RuleChatbotModal
                                                open={showRuleChatbot}
                                                onClose={() => setShowRuleChatbot(false)}
                                                availableColumns={[columnName]}
                                                columnSample={columnSample || []}
                                                hasExistingRule={rules && rules.length > 0}
                                                tableName={tableName || analysis?.fileName}
                                                tableMetadata={{
                                                    file_name: analysis?.fileName || 'data_table',
                                                    description: `Data quality rule creation for ${columnName}`,
                                                    columns: analysis?.columns?.map(col => ({
                                                        name: col.name,
                                                        type: col.detectedType?.toLowerCase() || 'string',
                                                        description: col.description || `Column: ${col.name}`
                                                    })) || [{
                                                        name: columnName,
                                                        type: 'string',
                                                        description: `Column: ${columnName}`
                                                    }]
                                                }}
                                                onFinalize={(suggestion) => {
                                                    // Append a new rule populated from LLM suggestion in one atomic update
                                                    const frontendType = BACKEND_TO_FRONTEND_RULE_TYPES[suggestion.rule_type] || 'CUSTOM_RULE';
                                                    // Produce a simple business meaning without prefixes
                                                    const rawDesc = (suggestion.rule_description || '').trim();
                                                    const stripped = rawDesc.replace(/^\s*generated\s*from\s*:\s*/i, '').replace(/^\s*generated\s*:\s*/i, '');
                                                    const isUniq = suggestion.rule_type.toLowerCase().includes('unique') || suggestion.rule_type.toLowerCase().includes('primary');
                                                    const params = suggestion.parameters as Record<string, unknown> | undefined;
                                                    const findVal = (keys: string[]) => keys.map(k => params?.[k]).find(v => typeof v === 'number' || typeof v === 'string');
                                                    const minVal = findVal(['min', 'minimum', 'min_value', 'lower_bound']);
                                                    const maxVal = findVal(['max', 'maximum', 'max_value', 'upper_bound']);
                                                    let businessMeaning: string;
                                                    if (isUniq) {
                                                        businessMeaning = 'Values in the specified column are unique across all records.';
                                                    } else if (minVal !== undefined && maxVal !== undefined) {
                                                        businessMeaning = `${columnName} must be between ${minVal} and ${maxVal}.`;
                                                    } else {
                                                        businessMeaning = stripped || `${columnName} will meet the specified data quality condition.`;
                                                    }
                                                    // Handle rule_logic based on scenario type
                                                    let ruleLogic: string;
                                                    if (suggestion.parameters?.custom_sql) {
                                                        // SQL scenario: create proper JSON structure with SQL and understanding
                                                        const ruleData = {
                                                            ...suggestion.parameters,
                                                            custom_sql: suggestion.parameters.custom_sql,
                                                            validation_description: suggestion.parameters.validation_description || suggestion.parameters.llm_understanding,
                                                            generated_by_llm: true,
                                                            original_prompt: suggestion.parameters.original_prompt || suggestion.rule_description
                                                        };
                                                        ruleLogic = JSON.stringify(ruleData);
                                                    } else if (suggestion.parameters?.llm_understanding || suggestion.parameters?.validation_description) {
                                                        // Fallback: LLM understanding without SQL - convert to JSON format
                                                        const ruleData = {
                                                            validation_description: suggestion.parameters.validation_description || suggestion.parameters.llm_understanding,
                                                            generated_by_llm: true,
                                                            original_prompt: suggestion.rule_description
                                                        };
                                                        ruleLogic = JSON.stringify(ruleData);
                                                    } else {
                                                        // Static scenario: use JSON.stringify of parameters
                                                        ruleLogic = JSON.stringify(suggestion.parameters || {});
                                                    }

                                                    setRules(prev => {
                                                        // Check if there's an empty rule to replace
                                                        const emptyRuleIndex = prev.findIndex(rule =>
                                                            rule.rule_type === '' && rule.rule_logic === '' && rule.business_meaning === ''
                                                        );

                                                        const newRule = {
                                                            column_name: columnName,
                                                            rule_type: frontendType,
                                                            rule_logic: ruleLogic,
                                                            business_meaning: businessMeaning,
                                                            active_flag: true,
                                                            visibility: 'public' as const, // ✅ Fixed visibility type
                                                            created_by: 'current_user',
                                                            created_at: new Date().toISOString()
                                                        };

                                                        if (emptyRuleIndex !== -1) {
                                                            // Replace the empty rule
                                                            const newRules = [...prev];
                                                            newRules[emptyRuleIndex] = newRule;
                                                            return newRules;
                                                        } else {
                                                            // No empty rule found, add new rule
                                                            return [...prev, newRule];
                                                        }
                                                    });
                                                    setShowRuleChatbot(false);
                                                }}
                                            />
                                        </ErrorBoundary>

                                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-lg">
                                            <table className="min-w-full">
                                                <thead>
                                                    <tr className="bg-gradient-to-r from-red-50 to-rose-50">
                                                        <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200 w-1/3">
                                                            {tRule('Rule Description')}
                                                        </th>
                                                        <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200 w-1/4">
                                                            {tRule('Rule Type')}
                                                        </th>
                                                        <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200 w-1/3">
                                                            {tRule('Rule Configuration')}
                                                        </th>
                                                        {showVisibility && (
                                                            <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200 w-28">
                                                                <div className="flex items-center justify-center space-x-1">
                                                                    <span>{tRule('Visibility')}</span>
                                                                    <BiSolidInfoCircle
                                                                        className="w-5 h-5 text-red-500 hover:text-red-700 cursor-help"
                                                                        title="Choose who can access this rule: Public (visible to all users) or Private (visible only to you)."
                                                                    />
                                                                </div>
                                                            </th>
                                                        )}
                                                        <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200 w-24">
                                                            {tRule('Actions')}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200">
                                                    {rules.map((rule, index) => {
                                                        return (
                                                            <tr key={index} className="hover:bg-gradient-to-r hover:from-red-50 hover:to-rose-50 transition-all duration-200 border-b border-gray-100">
                                                                <td className="px-4 py-3 border-b">
                                                                    <div className="space-y-1">
                                                                        <textarea
                                                                            value={rule.business_meaning || ''}
                                                                            onChange={(e) => updateRule(index, 'business_meaning', e.target.value)}
                                                                            placeholder="Enter rule description... (required)"
                                                                            rows={3}
                                                                            className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${!rule.business_meaning?.trim() && rule.rule_type
                                                                                ? 'border-red-300 bg-red-50'
                                                                                : 'border-gray-300'
                                                                                }`}
                                                                        />
                                                                        {!rule.business_meaning?.trim() && rule.rule_type && (
                                                                            <p className="text-xs text-red-600">
                                                                                ⚠️ Rule description is required
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3 border-b">
                                                                    <div className="flex items-center gap-2">
                                                                        <select
                                                                            value={rule.rule_type}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value;
                                                                                if (val === 'AI_RULE_CREATOR') {
                                                                                    setShowRuleChatbot(true);
                                                                                    // Do not set rule_type to avoid invalid save type
                                                                                    return;
                                                                                }
                                                                                updateRule(index, 'rule_type', val);
                                                                            }}
                                                                            disabled={!!rule.id} // Disable rule type changes for existing rules
                                                                            className={`flex-1 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${rule.id ? 'bg-gray-100 text-gray-600 cursor-not-allowed border-gray-300' : 'border-gray-300'} ${rule.rule_type === 'AI_RULE_CREATOR' ? 'text-red-700 border-red-500 bg-red-50' : ''}`}
                                                                            title={rule.id ? 'Rule type cannot be changed for existing rules' : ''}
                                                                        >
                                                                            <option value="" disabled>{tRule('Select validation rule...')}</option>
                                                                            {availableRules.map(ruleOption => (
                                                                                <option
                                                                                    key={ruleOption.rule_type}
                                                                                    value={ruleOption.rule_type}
                                                                                    title={ruleOption.business_meaning}
                                                                                    style={ruleOption.rule_type === 'AI_RULE_CREATOR' ? { color: '#dc2626', fontWeight: 600 } : undefined}
                                                                                >
                                                                                    {tRule(ruleOption.label)}
                                                                                </option>
                                                                            ))}
                                                                        </select>

                                                                        {/* Clean info icon with tooltip */}
                                                                        {rule.rule_type && (
                                                                            <div className="relative group">
                                                                                <BiSolidInfoCircle
                                                                                    className="w-5 h-5 text-red-500 hover:text-red-700 cursor-help transition-colors"
                                                                                />
                                                                                {/* Tooltip */}
                                                                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 shadow-lg">
                                                                                    <div className="font-semibold text-yellow-300 mb-1">What this rule does:</div>
                                                                                    <div>{availableRules.find(r => r.rule_type === rule.rule_type)?.business_meaning}</div>
                                                                                    {/* Tooltip arrow */}
                                                                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {/* Note removed per request */}
                                                                    {/* No prefilled helper text; user provides their own description */}
                                                                </td>
                                                                <td className="px-4 py-3 border-b">
                                                                    <RuleLogicInput
                                                                        ruleType={rule.rule_type}
                                                                        value={rule.rule_logic}
                                                                        onChange={(value) => updateRule(index, 'rule_logic', value)}
                                                                    />
                                                                </td>
                                                                {showVisibility && (
                                                                    <td className="px-4 py-3 border-b">
                                                                        <div className="flex items-center justify-start">
                                                                            <style>{switchStyles}</style>
                                                                            <label className="switch" title={rule.visibility === 'public' ? 'Public: visible to all users' : 'Private: visible only to you'}>
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={rule.visibility === 'private'}
                                                                                    onChange={(e) => {
                                                                                        console.log('🎯 Switch Clicked:');
                                                                                        console.log('  Checkbox checked:', e.target.checked);
                                                                                        console.log('  Current rule visibility:', rule.visibility);
                                                                                        console.log('  Will set visibility to:', e.target.checked ? 'private' : 'public');
                                                                                        updateRule(index, 'visibility', e.target.checked ? 'private' : 'public');
                                                                                    }}
                                                                                    aria-label={rule.visibility === 'public' ? 'Public: visible to all users' : 'Private: visible only to you'}
                                                                                />
                                                                                <span>PUBLIC</span>
                                                                                <span>PRIVATE</span>
                                                                            </label>
                                                                        </div>
                                                                    </td>
                                                                )}
                                                                <td className="px-2 py-3 border-b">
                                                                    <div className="flex items-center justify-center space-x-2">
                                                                        <button
                                                                            onClick={async () => {
                                                                                const newActiveFlag = !rule.active_flag;

                                                                                // For existing backend rules, call PATCH immediately
                                                                                if (rule.id && !rule.id.startsWith('temp_') && dataObjectId) {
                                                                                    setUpdatingRuleIds(prev => new Set([...prev, rule.id!]));
                                                                                    try {
                                                                                        await dqRuleService.updateRule(rule.id, { is_enabled: newActiveFlag });
                                                                                        updateRule(index, 'active_flag', newActiveFlag);
                                                                                        // Update originalRules so Save handler won't re-send this change
                                                                                        setOriginalRules(prev => prev.map(orig =>
                                                                                            orig.id === rule.id ? { ...orig, active_flag: newActiveFlag } : orig
                                                                                        ));
                                                                                    } catch (error) {
                                                                                        pushToast({ variant: 'error', message: 'Failed to update rule. Please try again.' });
                                                                                    } finally {
                                                                                        setUpdatingRuleIds(prev => {
                                                                                            const next = new Set(prev);
                                                                                            next.delete(rule.id!);
                                                                                            return next;
                                                                                        });
                                                                                    }
                                                                                } else {
                                                                                    // New/unsaved rules: local state only, saved with batch on Save
                                                                                    updateRule(index, 'active_flag', newActiveFlag);
                                                                                }
                                                                            }}
                                                                            className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors focus:outline-none ${rule.active_flag ? 'bg-green-600' : 'bg-gray-200'
                                                                                } ${rule.id && updatingRuleIds.has(rule.id) ? 'opacity-50' : ''}`}
                                                                            disabled={rule.id && updatingRuleIds.has(rule.id)}
                                                                            title={rule.active_flag ? 'Disable this rule' : 'Enable this rule'}
                                                                            aria-label={rule.active_flag ? 'Disable this rule' : 'Enable this rule'}
                                                                        >
                                                                            <span
                                                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${rule.active_flag ? 'translate-x-7' : 'translate-x-1'
                                                                                    }`}
                                                                            />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => {
                                                                                if (rule.id && dataObjectId) {
                                                                                    setDeleteConfirmation({ ruleId: rule.id, ruleIndex: index });
                                                                                } else {
                                                                                    removeRule(index);
                                                                                }
                                                                            }}
                                                                            className="text-red-600 hover:text-red-800 hover:bg-red-100 p-1 rounded-full transition-colors"
                                                                            title={rule.id ? "Delete rule (requires confirmation)" : "Remove rule"}
                                                                            disabled={false}
                                                                            style={{ opacity: 1 }}
                                                                        >
                                                                            <RiDeleteBin7Fill className="w-6 h-6" />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Enhanced footer */}
                                    <div className="flex justify-end items-center pt-6 border-t border-gray-200 bg-gradient-to-r from-red-50 to-rose-50 px-6 py-4 rounded-b-xl -mx-6">
                                        <div className="flex space-x-4">
                                            <button
                                                onClick={onClose}
                                                className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow"
                                            >
                                                {tRule('Cancel')}
                                            </button>
                                            <button
                                                onClick={handleSave}
                                                disabled={isSaving}
                                                className={`px-6 py-3 text-sm font-semibold text-white rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 ${isSaving
                                                    ? 'bg-gray-400 cursor-not-allowed'
                                                    : 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700'
                                                    }`}
                                            >
                                                {isSaving ? (
                                                    <span className="flex items-center">
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                        {tRule('Saving...')}
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center">
                                                        <RiSaveFill className="w-5 h-5 mr-2" />
                                                        {tRule('Save Rules')}
                                                    </span>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}

                            {activeTab === 'existing' && tableName && (
                                <div className="mb-6">
                                    <div className="flex justify-between items-center mb-4">
                                        <h4 className="text-lg font-semibold text-gray-900">
                                            {tRule('Available Rules from Other Columns')}
                                        </h4>
                                    </div>

                                    {/* Search Filter */}
                                    <div className="mb-4">
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                            </div>
                                            <input
                                                type="text"
                                                placeholder="Search rules by description, type, or column..."
                                                value={searchFilter}
                                                onChange={(e) => setSearchFilter(e.target.value)}
                                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                            />
                                        </div>
                                    </div>

                                    {/* Existing Rules List */}
                                    <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                                        {getCompatibleExistingRules().length === 0 ? (
                                            <div className="text-center py-8">
                                                <div className="w-16 h-16 mx-auto bg-gray-200 rounded-full flex items-center justify-center mb-4">
                                                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                </div>
                                                <p className="text-gray-500 text-sm">
                                                    {searchFilter ? tRule('No rules match your search criteria') : tRule('No existing rules available')}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {getCompatibleExistingRules().map((rule) => {
                                                    const isSelected = selectedExistingRules.has(rule.id!);
                                                    const frontendRuleType = BACKEND_TO_FRONTEND_RULE_TYPES[rule.rule_type] || rule.rule_type;

                                                    // Determine rule access and visibility
                                                    const currentUserEmail = authManager.getUserEmail();
                                                    const isOwnRule = rule.created_by?.toLowerCase() === currentUserEmail?.toLowerCase();
                                                    const isPrivateRule = rule.visibility === 'private';
                                                    const isPublicRule = rule.visibility === 'public';

                                                    return (
                                                        <div
                                                            key={rule.id}
                                                            className={`p-4 border rounded-lg cursor-pointer transition-all ${isSelected
                                                                ? 'border-red-500 bg-red-50 ring-2 ring-red-200'
                                                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                                                                }`}
                                                            onClick={() => handleExistingRuleSelect(rule.id!)}
                                                        >
                                                            <div className="flex items-start">
                                                                <div className="flex items-center h-5">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={() => { }} // Handled by parent click
                                                                        className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                                                                    />
                                                                </div>
                                                                <div className="ml-3 flex-1">
                                                                    <div className="flex items-center justify-between">
                                                                        <div className="flex items-center space-x-2">
                                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                                {frontendRuleType}
                                                                            </span>
                                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                                                From: <span className="notranslate">{rule.column_name}</span>
                                                                            </span>
                                                                            {/* Visibility Badge */}
                                                                            {isPrivateRule && (
                                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                                                                                    🔒 Private
                                                                                </span>
                                                                            )}
                                                                            {isPublicRule && (
                                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                                                                    🌐 Public
                                                                                </span>
                                                                            )}
                                                                            {/* Owner Badge */}
                                                                            {isOwnRule && (
                                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                                                                    👤 Your Rule
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <p className="mt-2 text-sm text-gray-700 notranslate">
                                                                        {rule.rule_description || 'No description available'}
                                                                    </p>
                                                                    <div className="mt-2 text-xs text-gray-500">
                                                                        Created by: {rule.created_by} • {new Date(rule.created_at).toLocaleDateString()}
                                                                        {isPrivateRule && isOwnRule && (
                                                                            <span className="ml-2 text-red-600 font-medium">• Only visible to you</span>
                                                                        )}
                                                                        {isPublicRule && (
                                                                            <span className="ml-2 text-green-600 font-medium">• Visible to everyone</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    {/* Apply Selected Rules Button */}
                                    {selectedExistingRules.size > 0 && (
                                        <div className="mt-4 flex justify-end">
                                            <button
                                                onClick={applyExistingRules}
                                                disabled={isSaving}
                                                className={`flex items-center px-4 py-2 text-sm font-semibold text-white rounded-lg shadow-lg transition-all duration-200 ${isSaving
                                                    ? 'bg-gray-400 cursor-not-allowed'
                                                    : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 hover:shadow-xl transform hover:scale-105'
                                                    }`}
                                            >
                                                {isSaving ? (
                                                    <>
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                        {tRule('Applying...')}
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                        Apply {selectedExistingRules.size} Rule{selectedExistingRules.size > 1 ? 's' : ''} to {columnName}
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Rules Marketplace and Smart Suggestions removed per latest direction */}
                        </>
                    )}
                </div>
            </div>

            {/* Phase 3: Delete Confirmation Modal */}
            {deleteConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center mb-4">
                            <div className="flex-shrink-0">
                                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                    <RiDeleteBin7Fill className="w-5 h-5 text-red-600" />
                                </div>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-lg font-medium text-gray-900">{tRule('Delete Rule')}</h3>
                            </div>
                        </div>
                        <div className="mb-6">
                            <p className="text-sm text-gray-500">
                                {tRule('Are you sure you want to delete this data quality rule? This action cannot be undone.')}
                            </p>
                        </div>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setDeleteConfirmation(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                disabled={updatingRuleIds.has(deleteConfirmation.ruleId)}
                            >
                                {tRule('Cancel')}
                            </button>
                            <button
                                onClick={() => deleteIndividualRule(deleteConfirmation.ruleId, deleteConfirmation.ruleIndex)}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
                                disabled={updatingRuleIds.has(deleteConfirmation.ruleId)}
                            >
                                {updatingRuleIds.has(deleteConfirmation.ruleId) ? tRule('Deleting...') : tRule('Delete Rule')}
                            </button>
                        </div>
                    </div>
                </div>
            )}


        </div>,
        document.body
    );
};

const ColumnConfigurationTable: React.FC<ColumnConfigurationTableProps> = ({
    analysis,
    onColumnUpdate,
    dataObjectId,
    // Note: Removed columnRulesCount parameter - only show user-applied and saved rules
    tableName,
    onRulesUpdated,
    onSelectionChange,
    initialSelectedColumns,
    existingRules = [],
    rulesLoading = false,
    showVisibility = !!tableName, // Show visibility only for Databricks tables (when tableName is provided)
    newColumnNames = []
}) => {
    // Column selection UI for scoped reporting
    const ENABLE_COLUMN_SELECTION = true;
    const { t } = useTranslate();
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedColumn, setSelectedColumn] = useState<string>('');
    // Local search query for filtering columns within this table
    const [localSearch, setLocalSearch] = useState('');
    // Column selection state
    const [selectedColumns, setSelectedColumns] = useState<Set<string>>(() => new Set(initialSelectedColumns || []));

    // Track whether we have received the first real data from the async metadata fetch.
    // Once synced, we stop overwriting so user interactions are preserved.
    const hasSyncedInitialSelection = useRef(false);

    useEffect(() => {
        const incoming = initialSelectedColumns || [];
        // Only sync once: when the prop first delivers real data (length > 0).
        if (!hasSyncedInitialSelection.current && incoming.length > 0) {
            hasSyncedInitialSelection.current = true;
            setSelectedColumns(new Set(incoming));
        }
    }, [initialSelectedColumns]);

    // Create a unique storage key for this data object/table
    // Use table-based storage so rules persist across different data objects for the same table
    const storageKey = `dq-rules-${tableName || dataObjectId || 'unknown'}`;

    // Initialize state from sessionStorage to survive component remounting
    const [savedRules, setSavedRules] = useState<Record<string, BackendDQRule[]>>(() => {
        try {
            const stored = sessionStorage.getItem(`${storageKey}-saved`);
            console.log('[ColumnConfigurationTable] Loading rules from sessionStorage for key:', storageKey);
            console.log('[ColumnConfigurationTable] Stored rules:', stored);

            if (stored) {
                const parsed = JSON.parse(stored);
                console.log('[ColumnConfigurationTable] Parsed stored rules:', parsed);
                return parsed;
            }
        } catch (error) {
            console.error('[ColumnConfigurationTable] Failed to parse stored rules:', error);
        }
        console.log('[ColumnConfigurationTable] No valid stored rules found, using empty state');
        return {};
    });

    // Track whether rules should be applied to columns or just available for selection
    // For new data objects, rules are available but not applied
    const [rulesAppliedToColumns, setRulesAppliedToColumns] = useState<Record<string, BackendDQRule[]>>(() => {
        // For new data object analysis (no dataObjectId), start with empty applied rules
        // Rules will be in savedRules for "Use Existing Rules" but not applied to columns
        if (!dataObjectId) {
            console.log('[ColumnConfigurationTable] New data object analysis - rules available but not applied to columns');
            return {};
        }

        // For existing data objects (editing), applied rules same as saved rules
        console.log('[ColumnConfigurationTable] Existing data object - applied rules same as saved rules');
        try {
            const stored = sessionStorage.getItem(`${storageKey}-saved`);
            if (stored) {
                const parsed = JSON.parse(stored);
                return parsed;
            }
        } catch (error) {
            console.error('[ColumnConfigurationTable] Failed to parse stored rules for applied rules:', error);
        }
        return {};
    });

    const [deletedRuleIds, setDeletedRuleIds] = useState<Set<string>>(() => {
        try {
            const stored = sessionStorage.getItem(`${storageKey}-deleted`);
            return stored ? new Set(JSON.parse(stored)) : new Set();
        } catch {
            return new Set();
        }
    });

    // Persist savedRules to sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem(`${storageKey}-saved`, JSON.stringify(savedRules));
    }, [savedRules, storageKey]);

    // Persist deletedRuleIds to sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem(`${storageKey}-deleted`, JSON.stringify(Array.from(deletedRuleIds)));
    }, [deletedRuleIds, storageKey]);

    // Merge existing rules from props into savedRules when they are loaded
    // ONLY for existing data objects (when editing), NOT for new data object creation
    useEffect(() => {
        console.log('[ColumnConfigurationTable] existingRules useEffect triggered');
        console.log('  - existingRules:', existingRules);
        console.log('  - existingRules.length:', existingRules?.length);
        console.log('  - rulesLoading:', rulesLoading);
        console.log('  - dataObjectId:', dataObjectId);
        console.log('  - typeof dataObjectId:', typeof dataObjectId);
        console.log('  - dataObjectId check result:', !dataObjectId);

        if (!existingRules || existingRules.length === 0 || rulesLoading) {
            console.log('[ColumnConfigurationTable] Early return - no rules or still loading');
            return;
        }

        // IMPORTANT: Do NOT auto-apply existing rules when creating NEW data objects
        // This prevents rules from being automatically applied when re-selecting the same
        // Databricks table after deletion. Rules should appear in "Use Existing Rules" section instead.

        // Only auto-apply existing rules if we have a dataObjectId (editing existing object)
        // For new objects (Databricks table analysis), rules should be manually selected
        if (!dataObjectId) {
            console.log('[ColumnConfigurationTable] ✅ Correctly skipping auto-apply of existing rules for new data object');
            console.log('  - Rules will appear in "Use Existing Rules" section for manual selection');
            return;
        }

        console.log('[ColumnConfigurationTable] ⚠️  Auto-applying existing rules for existing data object:', existingRules);

        // Group existing rules by column
        const rulesByColumn: Record<string, BackendDQRule[]> = {};
        existingRules.forEach(rule => {
            if (rule.column_name) {
                if (!rulesByColumn[rule.column_name]) {
                    rulesByColumn[rule.column_name] = [];
                }
                rulesByColumn[rule.column_name].push(rule);
            }
        });

        // Merge with current rulesAppliedToColumns, using fresh API data for known rules
        // but preserving any locally-created rules that haven't been saved to backend yet
        setRulesAppliedToColumns(prevAppliedRules => {
            const updatedRules = { ...prevAppliedRules };
            let hasChanges = false;

            Object.entries(rulesByColumn).forEach(([columnName, apiRules]) => {
                const existing = updatedRules[columnName] || [];

                // Collect IDs of rules coming from the API
                const apiRuleIds = new Set(apiRules.map(r => r.id));

                // Keep locally-created rules (no id or id not in API response)
                const localOnlyRules = existing.filter(r => !r.id || !apiRuleIds.has(r.id));

                // Always use fresh API data for known rules (fixes stale sessionStorage cache)
                const merged = [...apiRules, ...localOnlyRules];

                if (JSON.stringify(merged) !== JSON.stringify(existing)) {
                    updatedRules[columnName] = merged;
                    hasChanges = true;
                    console.log(`[ColumnConfigurationTable] Applied ${apiRules.length} existing rules for column: ${columnName}`);
                }
            });

            return hasChanges ? updatedRules : prevAppliedRules;
        });
    }, [existingRules, rulesLoading, dataObjectId]); // Added dataObjectId as dependency

    // Initialize savedRules from column data when component first loads
    useEffect(() => {
        const columns = analysis?.columns ?? [];
        const initialRules: Record<string, BackendDQRule[]> = {};
        let hasNewRules = false;

        columns.forEach(column => {
            // Skip if we already have rules for this column in rulesAppliedToColumns
            if (rulesAppliedToColumns[column.name] && rulesAppliedToColumns[column.name].length > 0) {
                return;
            }

            // ONLY auto-apply rules from column.rules JSON string (for existing data objects)
            // Do NOT auto-apply rules from analysis.allDqRules (for new Databricks tables)
            if (column.rules && typeof column.rules === 'string' && column.rules.trim().length > 0) {
                try {
                    const parsedRules = JSON.parse(column.rules) as DQRule[];

                    if (Array.isArray(parsedRules) && parsedRules.length > 0) {
                        // Convert DQRule[] to BackendDQRule[] format
                        const backendRules: BackendDQRule[] = parsedRules.map(rule => ({
                            id: rule.id || `rule-${Date.now()}-${Math.random()}`,
                            rule_type: rule.rule_type as any, // Will be validated later
                            parameters: {}, // Parse from rule_logic if needed
                            rule_description: rule.business_meaning || '',
                            data_object_id: dataObjectId || '',
                            column_name: column.name,
                            created_by: rule.created_by || '',
                            created_at: rule.created_at || new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                            is_active: rule.active_flag !== false,
                            is_enabled: rule.active_flag !== false
                        }));

                        initialRules[column.name] = backendRules;
                        hasNewRules = true;
                    }
                } catch (error) {
                    console.warn(`Failed to parse rules for column "${column.name}":`, error);
                }
            }
            // NOTE: Rules from analysis.allDqRules should appear in "Use Existing Rules" section,
            // not be automatically applied to savedRules. This allows users to manually select
            // which existing rules to apply when re-analyzing the same table.
        });

        // Update savedRules if we found new rules (for "Use Existing Rules" section)
        if (hasNewRules) {
            setSavedRules(prev => {
                const updated = { ...prev, ...initialRules };
                return updated;
            });

            // For existing data objects, also apply to columns
            if (dataObjectId) {
                setRulesAppliedToColumns(prev => {
                    const updated = { ...prev, ...initialRules };
                    return updated;
                });
            }
        }
    }, [analysis?.columns, savedRules]); // Added savedRules to dependencies to avoid re-running when already populated

    // Normalize columns to avoid early return before hooks
    const columns = analysis?.columns ?? [];

    // Extract all available DQ rules from the analysis API response and savedRules
    const getAllAvailableDqRules = (): BackendDQRule[] => {
        const allRules: BackendDQRule[] = [];

        console.log('[getAllAvailableDqRules] Starting extraction...');
        console.log('[getAllAvailableDqRules] analysis.allDqRules:', analysis?.allDqRules);
        console.log('[getAllAvailableDqRules] savedRules:', savedRules);

        // Add rules from savedRules (for previously created rules)
        Object.values(savedRules).forEach(columnRules => {
            allRules.push(...columnRules);
        });
        console.log('[getAllAvailableDqRules] Added', Object.values(savedRules).reduce((sum, rules) => sum + rules.length, 0), 'rules from savedRules');

        // Add rules from analysis.allDqRules (existing rules from database)
        if (analysis?.allDqRules && Array.isArray(analysis.allDqRules)) {
            console.log('[getAllAvailableDqRules] Found', analysis.allDqRules.length, 'rules in analysis.allDqRules');

            // Transform API rules to BackendDQRule format if needed
            const apiRules = analysis.allDqRules.map(rule => ({
                ...rule,
                // Ensure all required fields are present
                created_at: rule.created_at || new Date().toISOString(),
                updated_at: rule.updated_at || new Date().toISOString(),
                is_active: rule.is_active !== false, // Default to true
                is_enabled: rule.is_enabled !== false, // Default to true
                can_reuse: rule.can_reuse !== false // Default to true
            }));
            allRules.push(...apiRules);
            console.log('[getAllAvailableDqRules] Added', apiRules.length, 'rules from analysis.allDqRules');
        } else {
            console.log('[getAllAvailableDqRules] No allDqRules found in analysis');
        }

        console.log('[ColumnConfigurationTable] getAllAvailableDqRules found', allRules.length, 'total rules');

        return allRules;
    };

    // Apply local search filtering (by column name and description)
    const matchesSearch = (col: FileColumn) => {
        if (!localSearch.trim()) return true;
        const q = localSearch.toLowerCase();
        return col.name.toLowerCase().includes(q) || (col.description ? col.description.toLowerCase().includes(q) : false);
    };

    const displayedColumns = columns.filter(col => matchesSearch(col));

    // Persist selection changes to parent
    useEffect(() => {
        if (ENABLE_COLUMN_SELECTION && onSelectionChange) {
            onSelectionChange(Array.from(selectedColumns));
        }
    }, [selectedColumns, onSelectionChange, ENABLE_COLUMN_SELECTION]);

    const toggleColumnSelection = (name: string) => {
        setSelectedColumns(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    // Fallback: derive sample values from previewData when column.sampleValues is empty
    const getColumnSample = (name: string): unknown[] => {
        const col = columns.find(c => c.name === name);
        const direct = col?.sampleValues || [];
        if (direct && direct.length > 0) return direct;
        const rows = analysis?.previewData || [];
        const derived = rows
            .map(r => (r ? (r as Record<string, unknown>)[name] : undefined))
            .filter(v => v !== undefined && v !== null);
        return derived.slice(0, 6);
    };

    const selectAllDisplayed = () => {
        setSelectedColumns(prev => {
            const next = new Set(prev);
            displayedColumns.forEach(c => next.add(c.name));
            return next;
        });
    };

    const clearSelection = () => setSelectedColumns(new Set());

    const handleAddRule = (columnName: string) => {
        setSelectedColumn(columnName);
        setModalOpen(true);
    };

    const handleRulesUpdated = (columnName: string, newRules: BackendDQRule[]) => {
        // Store rules in local memory (for "Use Existing Rules" section)
        setSavedRules(prev => ({
            ...prev,
            [columnName]: newRules
        }));

        // Also apply to columns for display
        setRulesAppliedToColumns(prev => ({
            ...prev,
            [columnName]: newRules
        }));

        // Clear any deleted rule IDs for this column since we have fresh rules
        setDeletedRuleIds(prev => {
            const newSet = new Set(prev);
            // Remove any deleted IDs that might conflict with the new rules
            newRules.forEach(rule => {
                if (rule.id && newSet.has(rule.id)) {
                    newSet.delete(rule.id);
                }
            });
            return newSet;
        });

        // Also notify parent component if callback provided
        if (onRulesUpdated) {
            onRulesUpdated(columnName, newRules);
        }
    };

    const handleRuleDeleted = (columnName: string, ruleId: string) => {
        // Add rule ID to deleted rules set
        setDeletedRuleIds(prev => {
            const newSet = new Set(prev);
            newSet.add(ruleId);
            return newSet;
        });

        // Also remove from saved rules if it exists there (for newly created rules)
        setSavedRules(prev => {
            const currentColumnRules = prev[columnName] || [];
            const updatedColumnRules = currentColumnRules.filter(rule => rule.id !== ruleId);

            if (updatedColumnRules.length === 0 && prev[columnName]) {
                // Remove the column entirely if no rules left
                const next = { ...prev } as Record<string, BackendDQRule[]>;
                delete next[columnName];
                return next;
            } else if (prev[columnName]) {
                return {
                    ...prev,
                    [columnName]: updatedColumnRules
                };
            }
            return prev; // No changes needed if column wasn't in savedRules
        });

        // Also remove from applied rules
        setRulesAppliedToColumns(prev => {
            const currentColumnRules = prev[columnName] || [];
            const updatedColumnRules = currentColumnRules.filter(rule => rule.id !== ruleId);

            if (updatedColumnRules.length === 0 && prev[columnName]) {
                // Remove the column entirely if no rules left
                const next = { ...prev } as Record<string, BackendDQRule[]>;
                delete next[columnName];
                return next;
            } else if (prev[columnName]) {
                return {
                    ...prev,
                    [columnName]: updatedColumnRules
                };
            }
            return prev; // No changes needed if column wasn't in appliedRules
        });
    };

    const handleSaveRule = (rule: string) => {
        onColumnUpdate(selectedColumn, 'rules', rule);
    };

    // Get column rules - merge API rules with locally applied rules, filter out deleted rules
    const getColumnExistingRules = (columnName: string): BackendDQRule[] => {
        // Start with rules applied to columns and filter out deleted ones
        const appliedRules = rulesAppliedToColumns[columnName] || [];
        const filteredRules = appliedRules.filter(rule => !deletedRuleIds.has(rule.id));
        return filteredRules;
    };

    return (
        <div className="dd-table-container h-full">
            {/* Header with responsive column widths */}
            <table className="w-full table-fixed">
                <thead className="bg-brand-700 sticky top-0 z-10">
                    <tr>
                        <th className="text-fluid-sm font-bold text-white px-2 sm:px-4 py-2 text-center border-r border-brand-600" style={{ width: '30%' }}>{t('Column & Type')}</th>
                        <th className="text-fluid-sm font-bold text-white px-2 sm:px-4 py-2 text-center border-r border-brand-600" style={{ width: '50%' }}>
                            <div className="flex items-center justify-center gap-2">
                                <button
                                    type="button"
                                    aria-label="Information"
                                    className="flex items-center justify-center w-6 h-6 rounded bg-white/10 hover:bg-white/20 text-white shadow-sm border border-white/30"
                                    title="AI-generated descriptions - click to edit"
                                >
                                    <RiInformationFill className="w-4 h-4" />
                                </button>
                                <span>{t('Description')}</span>
                            </div>
                        </th>
                        <th className="text-fluid-sm font-bold text-white px-2 sm:px-4 py-2 text-center" style={{ width: '20%' }}>{t('Sample Values')}</th>
                    </tr>
                </thead>
            </table>
            {/* Inline column search (visible near Add Rule area) */}
            <div className="px-2 sm:px-4 py-1 bg-white border-x border-t border-brand-100">
                <div className="flex items-center gap-3">
                    <label className="text-xs font-medium text-black">{t('Search Columns')}</label>
                    <div className="relative flex-1">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </span>
                        <input
                            type="text"
                            value={localSearch}
                            onChange={(e) => setLocalSearch(e.target.value)}
                            placeholder="Search by column name or description..."
                            aria-label="Search columns"
                            className="block w-full pl-9 pr-9 py-1.5 border border-red-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-red-500 focus:border-red-500 text-black text-xs sm:text-sm"
                        />
                        {localSearch && (
                            <button
                                type="button"
                                onClick={() => setLocalSearch('')}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                                aria-label="Clear search"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                    {rulesLoading && <span className="text-[11px] text-blue-600 whitespace-nowrap">• Loading rules...</span>}
                    {ENABLE_COLUMN_SELECTION && (
                        <div className="ml-auto flex items-center gap-2">
                            <span className="text-[11px] text-gray-700">Selected: <span className="notranslate">{selectedColumns.size}/{displayedColumns.length}</span></span>
                            <button
                                type="button"
                                onClick={selectedColumns.size === displayedColumns.length ? clearSelection : selectAllDisplayed}
                                className={`px-2 py-1 rounded-md text-xs border ${selectedColumns.size === displayedColumns.length ? 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100' : 'border-gray-300 bg-white hover:bg-gray-50'}`}
                                title={selectedColumns.size === displayedColumns.length ? 'Unselect all columns' : 'Select all columns'}
                            >
                                {selectedColumns.size === displayedColumns.length ? 'Unselect All' : 'Select All'}
                            </button>
                        </div>
                    )}
                </div>
                {/* Filter chips removed as requested */}
            </div>
            <div className="dd-rows-scroll">
                <table className="w-full table-fixed">
                    <thead className="invisible">
                        <tr>
                            <th style={{ width: '30%' }}></th>
                            <th style={{ width: '50%' }}></th>
                            <th style={{ width: '20%' }}></th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-brand-100">
                        {displayedColumns.map((column, idx) => {
                            const isNewColumn = newColumnNames.includes(column.name);
                            return (
                                <tr key={column.name} className={`hover:bg-brand-50 transition-colors duration-200 dd-row ${isNewColumn ? 'border-l-4 border-l-green-400 bg-green-50/30' : ''}`}>
                                    <td className="border-r border-gray-200 dd-description p-2 sm:p-3" style={{ width: '30%' }}>
                                        <div className="space-y-2 sm:space-y-3">
                                            <div className="flex items-start space-x-2 sm:space-x-3">
                                                {ENABLE_COLUMN_SELECTION && (
                                                    <input
                                                        type="checkbox"
                                                        aria-label={`Select column ${column.name}`}
                                                        checked={selectedColumns.has(column.name)}
                                                        onChange={() => toggleColumnSelection(column.name)}
                                                        className="mt-0.5 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                                                    />
                                                )}
                                                <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-brand-100 rounded-full flex items-center justify-center text-xs font-bold text-brand-700">{idx + 1}</span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-xs sm:text-sm font-semibold text-gray-900 block truncate notranslate" title={column.name}>{column.name}</span>
                                                        {isNewColumn && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200 whitespace-nowrap">
                                                                NEW
                                                            </span>
                                                        )}
                                                    </div>
                                                    {column.hasQualityIssues && (
                                                        <span className="inline-flex items-center px-1.5 py-0.5 mt-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200" title="This column has quality issues detected by analysis">
                                                            ⚠️ Issues
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="ml-7 sm:ml-9">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-36 sm:w-44">
                                                        <DataTypeSelector
                                                            value={(column.selectedType || column.detectedType || 'String') as DataType}
                                                            onChange={(value) => onColumnUpdate(column.name, 'selectedType', value)}
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleAddRule(column.name)}
                                                        className="px-2.5 py-1 text-xs font-medium text-brand-600 bg-brand-50 border border-brand-200 rounded-md hover:bg-brand-100 hover:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 transition-colors duration-200"
                                                    >
                                                        {(() => {
                                                            const hasStringRules = column.rules && typeof column.rules === 'string' && (column.rules as string).trim().length > 0;
                                                            const existingCount = getColumnExistingRules(column.name).length;
                                                            // Only show user-saved rules count
                                                            return t('Add Rule');
                                                        })()}
                                                    </button>
                                                </div>
                                                {column.rules && typeof column.rules === 'string' && column.rules.trim() && (() => {
                                                    try {
                                                        const parsedRules = JSON.parse(column.rules as string) as DQRule[];
                                                        const activeRules = parsedRules.filter((r) => r.active_flag);
                                                        if (activeRules.length > 0) {
                                                            return (
                                                                <div className="mt-2">
                                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                                                        <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                                                                        {activeRules.length > 1 ? `${activeRules.length} Rules Applied` : 'Rule Applied'}
                                                                    </span>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    } catch {
                                                        // Fallback for old format
                                                        const ruleCount = (column.rules as string).split(/[;\n]/).filter(r => r.trim()).length;
                                                        if (ruleCount > 0) {
                                                            return (
                                                                <div className="mt-2">
                                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                                                        <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                                                                        {ruleCount > 1 ? `${ruleCount} Rules Applied` : 'Rule Applied'}
                                                                    </span>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    }
                                                })()}
                                                {/* Show rule indicator only for user-saved rules */}
                                                {(() => {
                                                    const userSavedRules = getColumnExistingRules(column.name);
                                                    // Filter to only show active/enabled rules in the indicator
                                                    const activeUserRules = userSavedRules.filter(rule => rule.is_enabled !== false);
                                                    const rulesCount = activeUserRules.length;

                                                    if (!column.rules && rulesCount > 0) {
                                                        return (
                                                            <div className="mt-2">
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800" title={`${rulesCount} active DQ rule(s) applied`}>
                                                                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                                                                    {rulesCount > 1 ? `${rulesCount} Rules Applied` : 'Rule Applied'}
                                                                </span>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="border-r border-gray-200 dd-description p-2 sm:p-3" style={{ width: '50%' }}>
                                        <AutosizeDescription
                                            value={column.description || ''}
                                            onChange={(val) => onColumnUpdate(column.name, 'description', val)}
                                            placeholder="Enter detailed column description..."
                                            counterInside={false}
                                            showCounter={false}
                                            collapseOnBlur={true}
                                            baseHeight={36}
                                            maxHeight={120}
                                            className="form-input text-xs sm:text-sm border-brand-200 focus:border-brand-400 focus:ring-brand-300 w-full resize-none"
                                            maxChars={1000}
                                            warningThreshold={950}
                                        />
                                    </td>
                                    <td className="dd-sample-values p-2 sm:p-3" style={{ width: '20%' }}>
                                        <div className="space-y-2">
                                            <div className="bg-gray-50 rounded-lg p-2 sm:p-3 border max-h-40 overflow-y-auto">
                                                {column.sampleValues && column.sampleValues.length > 0 ? (
                                                    <div className="space-y-1">
                                                        {column.sampleValues.slice(0, 3).map((value, i) => (
                                                            <div key={i} className="text-xs sm:text-sm text-gray-700 py-1 px-2 bg-white rounded border border-gray-200 font-mono sample-value-item break-all notranslate" title={String(value)}>
                                                                {String(value).length > 30 ? `${String(value).substring(0, 30)}...` : String(value)}
                                                            </div>
                                                        ))}
                                                        {/* Remaining count hint intentionally removed per UX request */}
                                                    </div>
                                                ) : (
                                                    <div className="text-xs sm:text-sm text-gray-500 italic">No sample data available</div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {modalOpen && selectedColumn ? (
                <RuleModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    columnName={selectedColumn}
                    columnDataType={columns.find(col => col.name === selectedColumn)?.selectedType || columns.find(col => col.name === selectedColumn)?.detectedType || 'String'}
                    currentRule={typeof columns.find(col => col.name === selectedColumn)?.rules === 'string'
                        ? (columns.find(col => col.name === selectedColumn)?.rules as string) || ''
                        : ''}
                    onSave={handleSaveRule}
                    dataObjectId={dataObjectId}
                    tableName={tableName}
                    existingRules={getColumnExistingRules(selectedColumn)}
                    allDqRules={getAllAvailableDqRules()}
                    onRulesUpdated={handleRulesUpdated}
                    onRuleDeleted={handleRuleDeleted}
                    columnSample={getColumnSample(selectedColumn)}
                    analysis={analysis}
                    showVisibility={showVisibility}
                />
            ) : null}
        </div>
    );
};

export default ColumnConfigurationTable;
